import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { inventoryAPI, shipstationAPI, orderAPI, adminAPI, productAPI, paymentAPI } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  Grid,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Tooltip,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Autocomplete,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  InputAdornment,
  Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Assignment as AssignIcon,
  QrCodeScanner as ScanIcon,
  Inventory2 as PackIcon,
  LocalShipping as ShipIcon,
  CheckCircle as CompleteIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  PlayArrow as StartIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import { inventoryService } from '../../services/inventoryService';
import { auditService } from '../../services/auditService';
import Breadcrumbs from '../../components/Breadcrumbs';
import { formatFullName, formatName } from '../../utils/nameUtils';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api';

const getCarrierTrackingUrl = (carrier, trackingNumber) => {
  if (!trackingNumber) return null;
  const normalized = (carrier || '').toUpperCase();

  if (normalized.includes('FEDEX')) {
    return `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
  }
  if (normalized.includes('UPS')) {
    return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  }
  if (normalized.includes('USPS')) {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  }
  if (normalized.includes('DHL')) {
    return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}&brand=DHL`;
  }

  // Fallback: package mapping or general search
  return `https://www.google.com/search?q=${encodeURIComponent(`${trackingNumber} shipment tracking`)}`;
};

const getCarrierDisplayName = (carrier) => {
  const normalized = (carrier || '').toUpperCase();
  if (normalized.includes('FEDEX')) return 'FedEx';
  if (normalized.includes('UPS')) return 'UPS';
  if (normalized.includes('USPS')) return 'USPS';
  if (normalized.includes('DHL')) return 'DHL';
  return carrier || 'Carrier';
};

const normalizeCarrierCode = (carrier) => {
  const normalized = (carrier || '').toLowerCase();
  if (normalized.includes('fedex')) return 'fedex';
  if (normalized.includes('ups')) return 'ups';
  return normalized;
};

const normalizeServiceCode = (service) => (service || '').toLowerCase();

const formatServiceName = (service) => {
  if (!service) return '';
  return service
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const ORDER_CREATION_STEPS = ['Select Customer', 'Add Products', 'Shipping & Review'];

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const createEmptyOrderItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productId: '',
  product: null,
  quantity: 1,
  unitPrice: '',
  isPriceOverridden: false
});

const createOrderItemFromProduct = (product, quantity = 1) => ({
  id: `${product?.id || 'product'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productId: product?.id || '',
  product: product || null,
  quantity,
  unitPrice: product?.basePrice ? Number(product.basePrice) : 0,
  isPriceOverridden: false
});

const SHIPPING_METHOD_OPTIONS = [
  {
    value: 'FEDEX_FIRST_OVERNIGHT',
    label: 'FedEx First Overnight (FREE)',
    description: 'By 8:30am delivery to most US addresses'
  },
  {
    value: 'FEDEX_STANDARD_OVERNIGHT',
    label: 'FedEx Standard Overnight (FREE)',
    description: 'By 10:30am delivery to most US addresses'
  },
  {
    value: 'UPS_NEXT_DAY_AIR_EARLY',
    label: 'UPS Next Day Air Early (FREE)',
    description: 'By 9:00am delivery to most US addresses'
  },
  {
    value: 'UPS_NEXT_DAY_AIR',
    label: 'UPS Next Day Air (FREE)',
    description: 'By 12:00pm delivery to most US addresses'
  }
];

const getShippingMethodLabel = (value) => {
  const match = SHIPPING_METHOD_OPTIONS.find((option) => option.value === value);
  return match ? match.label : value;
};

const getDefaultDeliveryDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

const PACKAGE_SIZE_OPTIONS = [
  { value: 'xs_box', label: 'XS Box (11x12x6)', length: 11, width: 12, height: 6 },
  { value: 's_box', label: 'S Box (11x12x9)', length: 11, width: 12, height: 9 },
  { value: 'm_box', label: 'M Box (13x13x13)', length: 13, width: 13, height: 13 },
  { value: 'l_box', label: 'L Box (16x16x16)', length: 16, width: 16, height: 16 }
];

const getPackageOption = (value) => PACKAGE_SIZE_OPTIONS.find(option => option.value === value);

const matchesPriorityOvernight = (serviceCode = '') => {
  const normalized = normalizeServiceCode(serviceCode);
  return normalized.includes('fedex') && normalized.includes('priority') && normalized.includes('overnight');
};

const matchesUpsNextDayAir = (serviceCode = '') => {
  const normalized = normalizeServiceCode(serviceCode);
  return normalized.includes('ups') && normalized.includes('next') && normalized.includes('day') && normalized.includes('air');
};

// Mock data for fallback - matches Azure database structure
const mockOrders = [
  // EPL Order 1 - PENDING
  {
    id: 'ORD-EPL-001',
    orderNumber: 'ORD-EPL-001',
    organization: 'EPL',
    client: 'Wellness Medical Group',
    orderDate: '2024-10-01T10:30:00Z',
    status: 'PENDING',
    priority: 'HIGH',
    totalAmount: 4200.00,
    items: [
      {
        id: 'item-1',
        productId: 'prod-epl-1',
        product: 'equicent™ CTM',
        sku: 'EQC-001',
        quantity: 10,
        pickedQuantity: 0,
        unitPrice: 420.00,
        totalPrice: 4200.00
      }
    ],
    shippingAddress: {
      name: 'Wellness Clinic',
      address1: '123 Medical Plaza',
      city: 'Dallas',
      state: 'TX',
      zipCode: '75201'
    },
    expectedDeliveryDate: '2025-10-15',
    notes: 'Rush delivery needed',
    assignedTo: null,
    trackingNumber: null
  },
  // EPL Order 2 - SHIPPED
  {
    id: 'ORD-EPL-002',
    orderNumber: 'ORD-EPL-002',
    organization: 'EPL',
    client: 'Wellness Medical Group',
    orderDate: '2024-09-25T08:00:00Z',
    status: 'SHIPPED',
    priority: 'NORMAL',
    totalAmount: 4035.50,
    items: [
      {
        id: 'item-2',
        productId: 'prod-epl-1',
        product: 'equicent™ CTM',
        sku: 'EQC-001',
        quantity: 10,
        pickedQuantity: 10,
        unitPrice: 420.00,
        totalPrice: 4200.00
      }
    ],
    shippingAddress: {
      name: 'Wellness Clinic',
      address1: '123 Medical Plaza',
      city: 'Dallas',
      state: 'TX',
      zipCode: '75201'
    },
    expectedDeliveryDate: '2025-10-12',
    notes: '',
    assignedTo: 'John Smith',
    trackingNumber: 'FEDEX-789456123'
  },
  // BARE Order - PICKING
  {
    id: 'ORD-BARE-001',
    orderNumber: 'ORD-BARE-001',
    organization: 'BARE',
    client: 'Elite Aesthetics',
    orderDate: '2024-09-28T14:00:00Z',
    status: 'PICKING',
    priority: 'NORMAL',
    totalAmount: 2660.00,
    items: [
      {
        id: 'item-3',
        productId: 'prod-bare-1',
        product: 'Lipoderma™',
        sku: 'LIP-001',
        quantity: 5,
        pickedQuantity: 3,
        unitPrice: 665.00,
        totalPrice: 2800.00
      }
    ],
    shippingAddress: {
      name: 'Elite Aesthetics',
      address1: '456 Wellness Blvd',
      city: 'Austin',
      state: 'TX',
      zipCode: '78701'
    },
    expectedDeliveryDate: '2025-10-18',
    notes: 'Client requested express shipping',
    assignedTo: 'Warehouse Manager',
    trackingNumber: null
  }
];

const mockInventory = [
  { sku: 'EQC-001', product: 'equicent™ CTM', organization: 'EPL', available: 40, batchId: 'BATCH-2024-Q3-001', expirationDate: '2025-12-31' },
  { sku: 'LIP-001', product: 'Lipoderma™', organization: 'BARE', available: 20, batchId: 'BATCH-2024-Q3-002', expirationDate: '2025-11-30' }
];

// Helper function to extract delivery date from client notes
const extractDeliveryDate = (notes) => {
  if (!notes) return null;
  
  // Look for "Delivery requested: YYYY-MM-DD" pattern
  const deliveryMatch = notes.match(/Delivery requested:\s*(\d{4}-\d{2}-\d{2})/i);
  if (deliveryMatch) {
    return deliveryMatch[1];
  }
  
  // Look for other date patterns
  const dateMatch = notes.match(/(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) {
    return dateMatch[1];
  }
  
  return null;
};

const extractShippingMethod = (notes) => {
  if (!notes) return { carrier: 'fedex_walleted', service: 'fedex_standard_overnight' };
  
  // Look for FedEx patterns
  if (notes.includes('FedEx') || notes.includes('FEDEX')) {
    if (notes.includes('Overnight') || notes.includes('OVERNIGHT')) {
      return { carrier: 'fedex_walleted', service: 'fedex_standard_overnight' };
    }
    if (notes.includes('2-Day') || notes.includes('2DAY')) {
      return { carrier: 'fedex_walleted', service: 'fedex_2day' };
    }
    return { carrier: 'fedex_walleted', service: 'fedex_standard_overnight' };
  }
  
  // Look for UPS patterns
  if (notes.includes('UPS')) {
    if (notes.includes('Overnight') || notes.includes('OVERNIGHT')) {
      return { carrier: 'ups_walleted', service: 'ups_next_day_air' };
    }
    if (notes.includes('2-Day') || notes.includes('2DAY')) {
      return { carrier: 'ups_walleted', service: 'ups_2nd_day_air' };
    }
    return { carrier: 'ups_walleted', service: 'ups_next_day_air' };
  }
  
  // Default to FedEx Overnight
  return { carrier: 'fedex_walleted', service: 'fedex_standard_overnight' };
};

const OrderProcessing = () => {
  const { org } = useOrg();
  const orgUpper = (org || '').toUpperCase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Active Queue, 1: In Transit, 2: Completed

  // Scanning state
  const [scannedSKU, setScannedSKU] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [scanSeverity, setScanSeverity] = useState('info');
  const scanInputRef = useRef(null);

  // Shipping state
  const [trackingNumber, setTrackingNumber] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [labelData, setLabelData] = useState(null); // Base64 encoded PDF label
  const [availableRates, setAvailableRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const [selectedRateId, setSelectedRateId] = useState('');
  const [selectedCarrierCode, setSelectedCarrierCode] = useState('');
  const [selectedServiceCode, setSelectedServiceCode] = useState('');
  const [selectedShipDate, setSelectedShipDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(null);
  const [requestedCarrierCode, setRequestedCarrierCode] = useState(null);
  const [requestedServiceCode, setRequestedServiceCode] = useState(null);
  const [shippingStatusMessage, setShippingStatusMessage] = useState('');
  const [shippingStatusSeverity, setShippingStatusSeverity] = useState('info');
  const [pickingSku, setPickingSku] = useState('');
  const [packageWeight, setPackageWeight] = useState('');
  const [invoiceSample, setInvoiceSample] = useState(false);
  const [invoiceTotalOverride, setInvoiceTotalOverride] = useState('');
  const [invoiceAdjustmentNote, setInvoiceAdjustmentNote] = useState('');
  const [updatingInvoice, setUpdatingInvoice] = useState(false);
  const [invoiceUpdateMessage, setInvoiceUpdateMessage] = useState(null);
  
  // Line items adjustment state
  const [lineItemsDialogOpen, setLineItemsDialogOpen] = useState(false);
  const [adjustedLineItems, setAdjustedLineItems] = useState([]);
  const [updatingLineItems, setUpdatingLineItems] = useState(false);
  const [lineItemsMessage, setLineItemsMessage] = useState(null);
  
  // Error dialog state
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState('');
  const [errorDialogMessage, setErrorDialogMessage] = useState('');
  const [errorDialogDetails, setErrorDialogDetails] = useState('');
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [createOrderStep, setCreateOrderStep] = useState(0);
  const [availableClients, setAvailableClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [orderItems, setOrderItems] = useState([createEmptyOrderItem()]);
  const [shippingDetails, setShippingDetails] = useState({
    name: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shippingMethod, setShippingMethod] = useState('FEDEX_FIRST_OVERNIGHT');
  const [orderNotes, setOrderNotes] = useState('');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createOrderError, setCreateOrderError] = useState('');
  const [createOrderSuccess, setCreateOrderSuccess] = useState(null);
  const [clientOrgMap, setClientOrgMap] = useState({});
  const resetCreateOrderState = () => {
    setCreateOrderStep(0);
    setAvailableClients([]);
    setClientsLoading(false);
    setAvailableProducts([]);
    setProductsLoading(false);
    setSelectedClient(null);
    setOrderItems([createEmptyOrderItem()]);
    setShippingDetails({
      name: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: ''
    });
    setDeliveryDate(getDefaultDeliveryDate());
    setShippingMethod('FEDEX_FIRST_OVERNIGHT');
    setOrderNotes('');
    setCreatingOrder(false);
    setCreateOrderError('');
    setCreateOrderSuccess(null);
  };

  const handleOpenCreateOrder = () => {
    if (orgUpper !== 'EPL') return;
    resetCreateOrderState();
    setCreateOrderOpen(true);
  };

  const handleCloseCreateOrder = () => {
    setCreateOrderOpen(false);
    resetCreateOrderState();
  };

  const prefillShippingFromClient = (client) => {
    if (!client) return;
    const profile = client.profile || {};
    setShippingDetails((prev) => ({
      ...prev,
      name: formatFullName(client) || profile.clinicName || prev.name,
      company: profile.clinicName || prev.company,
      address1: profile.address || prev.address1,
      address2: profile.address2 || prev.address2,
      city: profile.city || prev.city,
      state: profile.state || prev.state,
      zipCode: profile.zipCode || prev.zipCode,
      phone: client.phone || prev.phone
    }));
  };

  const fetchClientsForOrg = useCallback(async () => {
    if (!orgUpper) return;
    setClientsLoading(true);
    try {
      const response = await adminAPI.getUsers({
        organization: 'ALL',
        userType: 'CLIENT',
        status: 'ACTIVE',
        limit: 200
      });
      const users = response?.data?.users || [];
      const filteredUsers = users.filter((user) => {
        if (!user) return false;
        const directOrg = (user.organization || '').toUpperCase();
        if (directOrg) {
          if (orgUpper === 'EPL') {
            return directOrg === 'EPL';
          }
          if (orgUpper === 'BARE') {
            return directOrg === 'BARE';
          }
          return true;
        }
        const inferredOrg = (clientOrgMap[user.id] || '').toUpperCase();
        if (inferredOrg) {
          return inferredOrg === orgUpper;
        }
        return false;
      });
      setAvailableClients(filteredUsers);
    } catch (error) {
      console.error('Failed to load clients for organization', orgUpper, error);
      setAvailableClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [orgUpper, clientOrgMap]);

  const fetchProductsForOrg = useCallback(async () => {
    if (!orgUpper) return;
    setProductsLoading(true);
    try {
      const response = await productAPI.getProducts({
        organization: orgUpper === 'EPL' ? 'EPL' : 'BARE',
        limit: 100,
        includeInactive: false
      });
      const products = (response?.data || []).map((product) => ({
        ...product,
        basePrice: product.basePrice ? Number(product.basePrice) : 0
      }));
      setAvailableProducts(products);

      if (orgUpper === 'EPL' && products.length === 1) {
        const primaryProduct = products[0];
        setOrderItems((prev) => {
          const currentQuantity = prev && prev.length > 0 ? prev[0].quantity : 1;
          if (prev.length === 1 && prev[0].productId === primaryProduct.id) {
            return prev;
          }
          return [createOrderItemFromProduct(primaryProduct, currentQuantity)];
        });
      }
    } catch (error) {
      console.error('Failed to load products for organization', orgUpper, error);
      setAvailableProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [orgUpper]);

  const addOrderItem = () => {
    if (isSingleProductMode && availableProducts.length === 1) {
      // In single product mode, add another instance of the only product
      setOrderItems((prev) => [...prev, createOrderItemFromProduct(availableProducts[0])]);
    } else {
      setOrderItems((prev) => [...prev, createEmptyOrderItem()]);
    }
  };

  const updateOrderItemProduct = (id, product) => {
    setOrderItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              product,
              productId: product ? product.id : '',
              unitPrice: product?.basePrice ? Number(product.basePrice) : 0,
              isPriceOverridden: false
            }
          : item
      )
    );
  };

  const updateOrderItemPrice = (id, price) => {
    setOrderItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              unitPrice: price,
              isPriceOverridden: true
            }
          : item
      )
    );
  };

  const updateOrderItemQuantity = (id, quantity) => {
    const sanitized = Math.max(1, Number(quantity) || 1);
    setOrderItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: sanitized
            }
          : item
      )
    );
  };

  const removeOrderItem = (id) => {
    setOrderItems((prev) => (prev.length > 1 ? prev.filter((item) => item.id !== id) : prev));
  };

  const handleClientChange = (client) => {
    setSelectedClient(client);
    if (client) {
      prefillShippingFromClient(client);
    } else {
      setShippingDetails({
        name: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: ''
      });
    }
  };

  const handleShippingFieldChange = (field) => (event) => {
    const value = event.target.value;
    setShippingDetails((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const canProceedFromStep = (step) => {
    if (step === 0) {
      return Boolean(selectedClient);
    }
    if (step === 1) {
      const validItems = orderItems.filter((item) => item.productId && item.quantity > 0);
      return validItems.length > 0;
    }
    if (step === 2) {
      const { name, address1, city, state, zipCode } = shippingDetails;
      return Boolean(name && address1 && city && state && zipCode && deliveryDate && shippingMethod);
    }
    return true;
  };

  const handleNextCreateOrderStep = () => {
    if (!canProceedFromStep(createOrderStep)) {
      setCreateOrderError('Please complete all required information before continuing.');
      return;
    }
    setCreateOrderError('');
    setCreateOrderStep((prev) => Math.min(prev + 1, ORDER_CREATION_STEPS.length - 1));
  };

  const handleBackCreateOrderStep = () => {
    setCreateOrderError('');
    setCreateOrderStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmitOrder = async () => {
    if (!canProceedFromStep(2)) {
      setCreateOrderError('Shipping details are required to submit the order.');
      return;
    }

    const autoNote = deliveryDate
      ? `Delivery requested: ${deliveryDate}. Shipping method: ${getShippingMethodLabel(shippingMethod)}. Net 60 payment terms with 10% early payment discount available.`
      : `Shipping method: ${getShippingMethodLabel(shippingMethod)}. Net 60 payment terms with 10% early payment discount available.`;
    const combinedNotes = orderNotes
      ? `${orderNotes.trim()}\n${autoNote}`
      : autoNote;

    const payload = {
      clinicId: selectedClient?.profile?.clinicId || undefined,
      items: orderItems
        .filter((item) => item.productId && item.quantity > 0)
        .map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice !== '' && item.unitPrice !== undefined ? Number(item.unitPrice) : undefined
        })),
      deliveryDate: deliveryDate || undefined,
      shippingMethod,
      notes: combinedNotes,
      shippingAddress: {
        name: shippingDetails.name,
        company: shippingDetails.company || undefined,
        address1: shippingDetails.address1,
        address2: shippingDetails.address2 || undefined,
        city: shippingDetails.city,
        state: shippingDetails.state,
        zipCode: shippingDetails.zipCode,
        phone: shippingDetails.phone || undefined
      }
    };

    setCreatingOrder(true);
    setCreateOrderError('');
    setCreateOrderSuccess(null);

    try {
      // Step 1: Create the order first
      const response = await orderAPI.createOrder(payload);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to create order.');
      }
      const orderData = response.data || {};

      // Step 2: Calculate totals for Hyfin invoice
      const validItems = orderItems.filter((item) => item.productId && item.product);
      const subtotal = validItems.reduce((sum, item) => sum + Number(item.unitPrice || 0) * item.quantity, 0);
      const estimatedTax = 0; // No tax as per business requirement
      const estimatedShipping = 0; // No shipping as per business requirement
      const total = subtotal; // Total equals subtotal

      // Step 3: Create Hyfin invoice (same as client checkout flow)
      const customerName = shippingDetails.name || formatFullName(selectedClient) || 'Customer';
      const nameParts = customerName.split(' ');
      const invoiceData = {
        orderId: orderData.id,
        orderNumber: orderData.orderNumber || `ORD-${orderData.id.slice(-6)}`,
        customer: {
          firstName: nameParts[0] || 'Customer',
          lastName: nameParts.slice(1).join(' ') || 'Name',
          email: selectedClient?.email || 'customer@example.com',
          phone: shippingDetails.phone || selectedClient?.phone || '',
          address: {
            street1: shippingDetails.address1,
            street2: shippingDetails.address2 || '',
            city: shippingDetails.city,
            state: shippingDetails.state,
            zip: shippingDetails.zipCode,
            country: 'US'
          }
        },
        items: validItems.map(item => ({
          productId: item.productId,
          productName: item.product.name || 'EPL Product',
          unitPrice: Number(item.unitPrice || 0),
          quantity: item.quantity,
          total: Number(item.unitPrice || 0) * item.quantity
        })),
        totals: {
          subtotal,
          tax: estimatedTax,
          shipping: estimatedShipping,
          total
        },
        deliveryDate,
        shippingMethod,
        notes: combinedNotes
      };

      const invoiceResult = await paymentAPI.createInvoice(invoiceData);
      if (!invoiceResult.success) {
        throw new Error(invoiceResult.error || 'Failed to create Hyfin invoice');
      }

      setCreateOrderSuccess({
        id: orderData.id,
        orderNumber: orderData.orderNumber
      });
      await fetchOrders();
      setOrderItems([createEmptyOrderItem()]);
    } catch (error) {
      console.error('Failed to create order:', error);
      setCreateOrderError(error.message || 'Failed to create order. Please try again.');
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleNavigateToOrderProcessing = () => {
    if (createOrderSuccess?.id) {
      setCreateOrderOpen(false);
      resetCreateOrderState();
      navigate(`/internal/${orgUpper.toLowerCase()}/order-processing?order=${createOrderSuccess.id}`);
    } else {
      handleCloseCreateOrder();
    }
  };

  const handleCreateAnotherOrder = () => {
    resetCreateOrderState();
    setCreateOrderOpen(true);
  };
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completedOrderInfo, setCompletedOrderInfo] = useState(null);

  // Fetch orders from Azure SQL Database via API
  const fetchOrders = useCallback(async () => {
    if (!orgUpper) {
      return;
    }

    try {
      setLoading(true);
      const response = await orderAPI.getOrders({ page: 1, limit: 100 });
      const allOrders = (response.success && response.data) ? response.data : [];
      const orgOrders = allOrders.filter(order => order.organization === orgUpper);
      
      const transformedOrders = orgOrders.map(order => {
        // Determine client name from multiple possible sources
        let clientName = null;
        if (order.company?.name) {
          clientName = order.company.name;
        } else if (order.clinic?.name) {
          clientName = order.clinic.name;
        } else if (order.shippingCompany) {
          clientName = order.shippingCompany;
        } else if (order.shippingName) {
          clientName = order.shippingName;
        } else if (order.user?.firstName || order.user?.lastName) {
          clientName = formatFullName(order.user);
        }
        
        return {
          id: order.id,
          orderNumber: order.orderNumber,
          client: clientName || order.orderNumber,
          orderDate: order.orderDate,
          status: order.status,
          priority: order.priority || 'NORMAL',
          subtotal: Number(order.subtotal ?? 0),
          taxAmount: Number(order.taxAmount ?? 0),
          shippingAmount: Number(order.shippingAmount ?? 0),
          discountAmount: Number(order.discountAmount ?? 0),
          totalAmount: Number(order.totalAmount ?? 0),
          carrier: order.carrier || order.shippingCarrier || '',
          shippingMethod: order.shippingMethod || order.shippingService || '',
          items: (order.items || []).map(item => ({
            id: item.id,
            productId: item.productId,
            product: item.product?.name || 'Unknown Product',
            sku: item.product?.sku || '',
            quantity: item.quantity,
            pickedQuantity: item.pickedQuantity || 0,
            unitPrice: parseFloat(item.unitPrice),
            totalPrice: parseFloat(item.totalPrice)
          })),
          shippingAddress: {
            name: order.clinic?.name || order.shippingName || '',
            address1: order.clinic?.address || order.shippingAddress1 || '',
            city: order.clinic?.city || order.shippingCity || '',
            state: order.clinic?.state || order.shippingState || '',
            zipCode: order.clinic?.zipCode || order.shippingZipCode || ''
          },
          expectedDeliveryDate: order.expectedDeliveryDate || extractDeliveryDate(order.notes),
          notes: order.notes,
          assignedTo: order.assignedTo,
          trackingNumber: order.trackingNumber,
          isSample: Boolean(order.isSample),
          sampleReason: order.sampleReason || '',
          hyfinInvoiceId: order.hyfinInvoiceId || null,
          hyfinPaymentLink: order.hyfinPaymentLink || null
        };
      });

      setOrders(transformedOrders);
      const orgLookup = {};
      orgOrders.forEach(orderRecord => {
        if (orderRecord.user?.id && orderRecord.organization) {
          orgLookup[orderRecord.user.id] = orderRecord.organization.toUpperCase();
        }
      });
      setClientOrgMap(orgLookup);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Using demo data - backend deployment in progress');
      setOrders(mockOrders.filter(order => order.organization === orgUpper));
    } finally {
      setLoading(false);
    }
  }, [orgUpper]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (createOrderOpen && orgUpper === 'EPL') {
      fetchClientsForOrg();
      fetchProductsForOrg();
    }
  }, [createOrderOpen, orgUpper, fetchClientsForOrg, fetchProductsForOrg]);

  useEffect(() => {
    if (selectedClient && availableClients.length > 0) {
      const match = availableClients.find((client) => client.id === selectedClient.id);
      if (match && match !== selectedClient) {
        setSelectedClient(match);
        prefillShippingFromClient(match);
      }
    }
  }, [availableClients, selectedClient]);
  const isSingleProductMode = orgUpper === 'EPL' && availableProducts.length === 1;


  // Load inventory from received inventory workflow
  useEffect(() => {
    const loadInventory = async () => {
      if (!org) return;

      try {
        const skuResponse = await inventoryAPI.getSKUs(org?.toUpperCase(), 500);
        const skuData = skuResponse.data || [];
        
        const availableInventory = skuData
          .filter(sku => sku.status === 'AVAILABLE')
          .map(sku => {
            const tissueId = (sku.tissueId || `${sku.inventory?.lotNumber || ''}-${sku.sku}` || sku.sku || '').trim();
            return {
              sku: tissueId,
              tissueId,
              baseSku: sku.sku,
              productId: sku.productId,
              productName: sku.product?.name || 'Unknown Product',
              inventoryId: sku.inventoryId, // This is the actual inventory batch ID
              batchId: sku.inventory?.batchId || sku.inventoryId,
              lotNumber: sku.inventory?.lotNumber || 'Unknown Lot',
              available: 1, // Each tissue ID represents 1 unit
              allocated: 0,
              organization: sku.organization,
              location: [sku.warehouse, sku.zone, sku.shelf, sku.bin].filter(Boolean).join('-') || 'Unknown',
              costPerUnit: sku.costPerUnit || 0,
              expirationDate: sku.inventory?.expirationDate,
              status: sku.status,
              qcStatus: sku.inventory?.qcStatus || 'APPROVED'
            };
          });
        
        setInventory(availableInventory);
      } catch (err) {
        console.error('❌ Error loading inventory:', err);
        setInventory([]);
      }
    };

    loadInventory();
  }, [org]);

  // Auto-focus scan input
  useEffect(() => {
    if (currentStep === 1 && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [currentStep]);

  // Auto-open if order ID in URL
  useEffect(() => {
    if (orderId && orders.length > 0) {
      const order = orders.find(o => o.id === orderId);
      if (order) {
        handleStartProcessing(order);
      }
    }
  }, [orderId, orders]);

  useEffect(() => {
    if (!selectedOrder) {
      setAvailableRates([]);
      setSelectedRateId('');
      setRatesError('');
      setSelectedCarrierCode('');
      setSelectedServiceCode('');
      setRequestedCarrierCode(null);
      setRequestedServiceCode(null);
      setRequestedDeliveryDate(null);
      setSelectedShipDate(new Date().toISOString().split('T')[0]);
      setShippingStatusMessage('');
    setShippingStatusSeverity('info');
      setSelectedPackage('');
      setPackageWeight('');
      setInvoiceSample(false);
      setInvoiceTotalOverride('');
      setInvoiceAdjustmentNote('');
      setInvoiceUpdateMessage(null);
      setUpdatingInvoice(false);
      return;
    }

    const fallbackShipping = extractShippingMethod(selectedOrder.notes);
    const preferredCarrier = selectedOrder.carrier || fallbackShipping.carrier;
    const preferredService = selectedOrder.shippingMethod || fallbackShipping.service;

    setSelectedCarrierCode(preferredCarrier);
    setSelectedServiceCode(preferredService);
    setRequestedCarrierCode(preferredCarrier);
    setRequestedServiceCode(preferredService);
    setRequestedDeliveryDate(selectedOrder.expectedDeliveryDate || extractDeliveryDate(selectedOrder.notes));
    setSelectedShipDate(new Date().toISOString().split('T')[0]);
    setAvailableRates([]);
    setSelectedRateId('');
    setRatesError('');
    setShippingStatusSeverity('info');
    setSelectedPackage('');
    setPackageWeight('');
    const currentTotal = typeof selectedOrder.totalAmount === 'number'
      ? selectedOrder.totalAmount
      : Number(selectedOrder.totalAmount || 0);
    const initialSample = Boolean(selectedOrder.isSample) || currentTotal === 0;
    setInvoiceSample(initialSample);
    setInvoiceTotalOverride((initialSample ? 0 : currentTotal).toFixed(2));
    setInvoiceAdjustmentNote(selectedOrder.sampleReason || '');
    setInvoiceUpdateMessage(null);
  }, [selectedOrder?.id]);

  useEffect(() => {
    if (!processDialogOpen) {
      setShippingStatusMessage('');
      setRatesLoading(false);
      setRatesError('');
      setSelectedRateId('');
      setAvailableRates([]);
      setPickingSku('');
    }
  }, [processDialogOpen]);

  // Test function for previewing error dialog (can be called from browser console)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.testInsufficientBalanceDialog = () => {
        setErrorDialogTitle('Insufficient ShipStation Account Balance');
        setErrorDialogMessage('Your ShipStation account does not have sufficient funds to create this shipping label.');
        setErrorDialogDetails(
          'To continue, please add funds to your ShipStation account. ' +
          'Once your account balance is sufficient, you can generate the shipping label again. ' +
          'For FedEx Wallet carriers, you may also need to ensure your carrier account is properly configured.'
        );
        setErrorDialogOpen(true);
        console.log('✅ Test dialog opened! You can close it by clicking "I Understand" or the X button.');
      };
      
      window.testGenericErrorDialog = () => {
        setErrorDialogTitle('Label Generation Failed');
        setErrorDialogMessage('Unable to generate shipping label');
        setErrorDialogDetails('An unexpected error occurred while creating the shipping label. Please try again or contact support if the issue persists.');
        setErrorDialogOpen(true);
        console.log('✅ Test dialog opened! You can close it by clicking "I Understand" or the X button.');
      };
      
      console.log('🧪 Test functions available:');
      console.log('  - window.testInsufficientBalanceDialog() - Preview insufficient balance dialog');
      console.log('  - window.testGenericErrorDialog() - Preview generic error dialog');
    }
  }, []);

  const handleStartProcessing = (order) => {
    // Auto-assign to current user
    const updatedOrder = {
      ...order,
      assignedTo: 'current-user-id', // Will be replaced with real user ID from auth
      assignedAt: new Date().toISOString(),
      status: order.status === 'PENDING' ? 'ASSIGNED' : order.status // Preserve existing status if continuing
    };

    setSelectedOrder(updatedOrder);
    setProcessDialogOpen(true);
    
    // Load tracking number and label data if they exist (for resuming)
    if (order.trackingNumber) {
      setTrackingNumber(order.trackingNumber);
      // Note: labelData is base64 PDF stored in the order, but we'd need to fetch it
      // For now, if tracking number exists, we can proceed to complete
      // The label can be regenerated or fetched from ShipStation if needed
    }
    
    // Determine the correct step based on order status and progress
    if (order.status === 'PENDING') {
      setCurrentStep(0); // Start from beginning for new orders
    } else if (order.status === 'ASSIGNED') {
      setCurrentStep(0); // Still at start
    } else if (order.status === 'PICKING' || order.pickingStartedAt) {
      setCurrentStep(1); // Continue from picking step
    } else if (order.pickingCompletedAt && !order.packedAt) {
      setCurrentStep(2); // Continue from packing step
    } else if (order.packedAt && !order.hyfinInvoiceId) {
      setCurrentStep(3); // Continue from invoicing step (invoice not finalized yet)
    } else if (order.hyfinInvoiceId && !order.trackingNumber) {
      setCurrentStep(4); // Continue from shipping step (label not generated yet)
    } else if (order.trackingNumber && order.status !== 'SHIPPED' && order.status !== 'DELIVERED') {
      setCurrentStep(4); // Label generated, show complete button
    } else {
      setCurrentStep(0); // Default fallback
    }
  };

  const fetchCarrierRates = useCallback(async (force = false) => {
    if (!selectedOrder?.id) {
      return;
    }

    if (ratesLoading && !force) {
      return;
    }

    setRatesLoading(true);
    setRatesError('');

    try {
      const response = await shipstationAPI.getRates(selectedOrder.id);
      const payload = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const fallbackShipping = extractShippingMethod(selectedOrder.notes);
      const preferredCarrier = selectedOrder.carrier || response?.meta?.requestedCarrier || fallbackShipping.carrier;
      const preferredService = selectedOrder.shippingMethod || response?.meta?.requestedService || fallbackShipping.service;

      setRequestedDeliveryDate(
        response?.meta?.requestedDeliveryDate ||
        selectedOrder.expectedDeliveryDate ||
        extractDeliveryDate(selectedOrder.notes)
      );
      setRequestedCarrierCode(response?.meta?.requestedCarrier || preferredCarrier);
      setRequestedServiceCode(response?.meta?.requestedService || preferredService);

      const filteredRates = Array.isArray(payload)
        ? payload.filter((rate) => {
            const carrier = normalizeCarrierCode(rate?.carrierCode);
            const serviceCode = rate?.serviceCode || '';
            if (carrier === 'fedex') {
              return matchesPriorityOvernight(serviceCode);
            }
            if (carrier === 'ups') {
              return matchesUpsNextDayAir(serviceCode);
            }
            return false;
          })
        : [];

      const normalizedRates = filteredRates
        .map((rate) => ({
          id: `${rate?.carrierCode || ''}::${rate?.serviceCode || ''}`,
          carrierCode: rate?.carrierCode,
          serviceCode: rate?.serviceCode,
          serviceName: rate?.serviceName || rate?.serviceCode,
          shipmentCost:
            rate?.shipmentCost !== undefined && rate?.shipmentCost !== null
              ? Number(rate.shipmentCost)
              : null,
          otherCost:
            rate?.otherCost !== undefined && rate?.otherCost !== null
              ? Number(rate.otherCost)
              : null,
          deliveryDays: rate?.deliveryDays,
          estimatedDeliveryDate: rate?.estimatedDeliveryDate,
          carrierFriendlyName:
            rate?.carrierFriendlyName || rate?.carrierNickname || getCarrierDisplayName(rate?.carrierCode),
          requestDetails: {
            weight: rate?.requested?.weight || null,
            dimensions: rate?.requested?.dimensions || null
          }
        }))
        .reduce((acc, rate) => {
          const key = matchesPriorityOvernight(rate.serviceCode) ? 'fedex' : matchesUpsNextDayAir(rate.serviceCode) ? 'ups' : rate.id;
          if (!acc.some(existing => existing.key === key)) {
            acc.push({ key, ...rate });
          } else {
            const existingIndex = acc.findIndex(existing => existing.key === key);
            const existingRate = acc[existingIndex];
            const existingCost = existingRate.shipmentCost ?? Number.POSITIVE_INFINITY;
            const newCost = rate.shipmentCost ?? Number.POSITIVE_INFINITY;
            if (newCost < existingCost) {
              acc[existingIndex] = { key, ...rate };
            }
          }
          return acc;
        }, [])
        .map(({ key, ...rest }) => rest);

      setAvailableRates(normalizedRates);

      if (normalizedRates.length > 0) {
        const preferredRate = normalizedRates.find((rate) =>
          normalizeCarrierCode(rate.carrierCode) === normalizeCarrierCode(preferredCarrier) &&
          normalizeServiceCode(rate.serviceCode) === normalizeServiceCode(preferredService)
        );
        const defaultRate = preferredRate || normalizedRates[0];
        setSelectedRateId(defaultRate.id);
        setSelectedCarrierCode(defaultRate.carrierCode);
        setSelectedServiceCode(defaultRate.serviceCode);
      } else {
        setSelectedRateId('');
      }
    } catch (error) {
      console.error('Failed to load carrier rates:', error);
      setRatesError(error.message || 'Failed to load carrier rates');
      setAvailableRates([]);
      setSelectedRateId('');
    } finally {
      setRatesLoading(false);
    }
  }, [selectedOrder]);

  useEffect(() => {
    if (
      processDialogOpen &&
      currentStep === 2 &&
      selectedOrder?.id &&
      !ratesLoading &&
      availableRates.length === 0
    ) {
      fetchCarrierRates(true);
    }
  }, [processDialogOpen, currentStep, selectedOrder?.id, fetchCarrierRates, ratesLoading, availableRates.length]);

  const computedRateOptions = useMemo(() => {
    if (availableRates.length > 0) {
      return availableRates;
    }

    const fedexCarrier =
      normalizeCarrierCode(selectedCarrierCode) === 'fedex'
        ? selectedCarrierCode
        : normalizeCarrierCode(requestedCarrierCode) === 'fedex'
          ? requestedCarrierCode
          : 'fedex_walleted';

    const fedexServiceCandidate =
      normalizeCarrierCode(selectedCarrierCode) === 'fedex'
        ? selectedServiceCode
        : normalizeCarrierCode(requestedCarrierCode) === 'fedex'
          ? requestedServiceCode
          : null;

    const fedexService = matchesPriorityOvernight(fedexServiceCandidate)
      ? fedexServiceCandidate
      : 'fedex_priority_overnight';

    const upsCarrier =
      normalizeCarrierCode(selectedCarrierCode) === 'ups'
        ? selectedCarrierCode
        : normalizeCarrierCode(requestedCarrierCode) === 'ups'
          ? requestedCarrierCode
          : 'ups_walleted';

    const upsServiceCandidate =
      normalizeCarrierCode(selectedCarrierCode) === 'ups'
        ? selectedServiceCode
        : normalizeCarrierCode(requestedCarrierCode) === 'ups'
          ? requestedServiceCode
          : null;

    const upsService = matchesUpsNextDayAir(upsServiceCandidate)
      ? upsServiceCandidate
      : 'ups_next_day_air';

    return [
      {
        id: `${fedexCarrier}::${fedexService}`,
        carrierCode: fedexCarrier,
        serviceCode: fedexService,
        serviceName: formatServiceName(fedexService) || 'FedEx Priority Overnight (manual)',
        shipmentCost: null,
        otherCost: null,
        deliveryDays: null,
        estimatedDeliveryDate: null,
        carrierFriendlyName: 'FedEx'
      },
      {
        id: `${upsCarrier}::${upsService}`,
        carrierCode: upsCarrier,
        serviceCode: upsService,
        serviceName: formatServiceName(upsService) || 'UPS Next Day Air (manual)',
        shipmentCost: null,
        otherCost: null,
        deliveryDays: null,
        estimatedDeliveryDate: null,
        carrierFriendlyName: 'UPS'
      }
    ];
  }, [
    availableRates,
    requestedCarrierCode,
    requestedServiceCode,
    selectedCarrierCode,
    selectedServiceCode
  ]);

  const handleRateSelectionChange = (rateId) => {
    setSelectedRateId(rateId);
    const selected = computedRateOptions.find(option => option.id === rateId);
    if (selected) {
      setSelectedCarrierCode(selected.carrierCode);
      setSelectedServiceCode(selected.serviceCode);
    }
  };

  const handleInvoiceSampleToggle = (event) => {
    const checked = event.target.checked;
    setInvoiceSample(checked);
    setInvoiceUpdateMessage(null);
    if (checked) {
      setInvoiceTotalOverride('0.00');
    } else if (selectedOrder) {
      const currentTotal = typeof selectedOrder.totalAmount === 'number'
        ? selectedOrder.totalAmount
        : Number(selectedOrder.totalAmount || 0);
      setInvoiceTotalOverride(currentTotal.toFixed(2));
    }
  };

  const handleInvoiceTotalChange = (event) => {
    setInvoiceUpdateMessage(null);
    setInvoiceTotalOverride(event.target.value);
  };

  const handleHyfinInvoiceUpdate = async () => {
    if (!selectedOrder) {
      return;
    }

    const parsedTotal = Number(invoiceTotalOverride || 0);
    if (!invoiceSample) {
      if (invoiceTotalOverride === '' || Number.isNaN(parsedTotal) || parsedTotal < 0) {
        setInvoiceUpdateMessage({ severity: 'error', text: 'Enter a valid invoice total before updating.' });
        return;
      }
    }

    try {
      setUpdatingInvoice(true);
      setInvoiceUpdateMessage(null);

      const payload = {
        isSample: invoiceSample,
        overrideTotal: invoiceSample ? 0 : parsedTotal,
        reason: invoiceAdjustmentNote.trim() ? invoiceAdjustmentNote.trim() : undefined
      };

      const response = await paymentAPI.updateInvoiceTotals(selectedOrder.id, payload);
      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to update Hyfin invoice.');
      }

      const data = response.data || {};
      const updatedTotal = Number(
        typeof data.totalAmount === 'number'
          ? data.totalAmount
          : data.totalAmount !== undefined
            ? Number(data.totalAmount)
            : parsedTotal ?? 0
      );
      setInvoiceUpdateMessage({
        severity: 'success',
        text: response.message || 'Hyfin invoice updated successfully.'
      });

      const normalizedTotal = Number.isNaN(updatedTotal) ? 0 : updatedTotal;

      setSelectedOrder(prev => prev ? {
        ...prev,
        totalAmount: normalizedTotal,
        discountAmount: typeof data.discountAmount === 'number' ? data.discountAmount : prev.discountAmount,
        isSample: typeof data.isSample === 'boolean' ? data.isSample : prev.isSample,
        sampleReason: data.sampleReason ?? prev.sampleReason,
        hyfinInvoiceId: data.hyfinInvoiceId ?? prev.hyfinInvoiceId,
        hyfinPaymentLink: data.hyfinPaymentLink ?? prev.hyfinPaymentLink
      } : prev);

      setInvoiceSample(Boolean(data.isSample));
      setInvoiceTotalOverride(normalizedTotal.toFixed(2));
      if (data.sampleReason) {
        setInvoiceAdjustmentNote(data.sampleReason);
      }

      await fetchOrders();
    } catch (error) {
      setInvoiceUpdateMessage({
        severity: 'error',
        text: error.message || 'Failed to update Hyfin invoice.'
      });
    } finally {
      setUpdatingInvoice(false);
    }
  };

  const selectedPackageOption = useMemo(
    () => getPackageOption(selectedPackage),
    [selectedPackage]
  );

  const effectivePackageOption = useMemo(
    () => selectedPackageOption || getPackageOption(selectedOrder?.packageSize),
    [selectedPackageOption, selectedOrder?.packageSize]
  );

  const displayPackageWeight = useMemo(() => {
    if (packageWeight && !Number.isNaN(parseFloat(packageWeight))) {
      return parseFloat(packageWeight);
    }
    if (selectedOrder?.packageWeightLbs && !Number.isNaN(selectedOrder.packageWeightLbs)) {
      return selectedOrder.packageWeightLbs;
    }
    return null;
  }, [packageWeight, selectedOrder?.packageWeightLbs]);

  const parsedInvoiceOverride = useMemo(() => {
    if (invoiceSample) {
      return 0;
    }
    if (invoiceTotalOverride === '') {
      return null;
    }
    const value = Number(invoiceTotalOverride);
    return Number.isNaN(value) ? null : value;
  }, [invoiceSample, invoiceTotalOverride]);

  const invoiceHasPendingChanges = useMemo(() => {
    if (!selectedOrder) {
      return false;
    }
    const orderTotal = Number(selectedOrder.totalAmount ?? 0);
    const orderSample = Boolean(selectedOrder.isSample);
    const sampleChanged = invoiceSample !== orderSample;
    const totalChanged = !invoiceSample && parsedInvoiceOverride !== null && Math.abs(parsedInvoiceOverride - orderTotal) > 0.009;
    const noteChanged = invoiceSample && (invoiceAdjustmentNote.trim() || '') !== (selectedOrder.sampleReason || '');
    return sampleChanged || totalChanged || noteChanged;
  }, [selectedOrder, invoiceSample, parsedInvoiceOverride, invoiceAdjustmentNote]);

  const invoiceUpdateDisabled = useMemo(() => {
    if (updatingInvoice) {
      return true;
    }
    if (invoiceSample) {
      return !invoiceHasPendingChanges;
    }
    if (invoiceTotalOverride === '' || parsedInvoiceOverride === null) {
      return true;
    }
    if (parsedInvoiceOverride < 0) {
      return true;
    }
    return !invoiceHasPendingChanges;
  }, [invoiceSample, invoiceTotalOverride, parsedInvoiceOverride, updatingInvoice, invoiceHasPendingChanges]);

  // Initialize adjusted line items from order
  const initializeLineItems = useCallback(() => {
    if (!selectedOrder || !selectedOrder.items) {
      return;
    }
    
    const items = selectedOrder.items.map(item => ({
      id: item.id || `temp-${Date.now()}-${Math.random()}`,
      productId: item.productId,
      productName: item.product || 'Unknown Product',
      sku: item.sku || '',
      quantity: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      totalPrice: item.totalPrice || (item.unitPrice || 0) * (item.quantity || 0),
    }));
    
    setAdjustedLineItems(items);
  }, [selectedOrder]);

  // Open line items dialog
  const handleOpenLineItemsDialog = () => {
    initializeLineItems();
    setLineItemsDialogOpen(true);
    setLineItemsMessage(null);
  };

  // Close line items dialog
  const handleCloseLineItemsDialog = () => {
    setLineItemsDialogOpen(false);
    setLineItemsMessage(null);
  };

  // Add new line item
  const handleAddLineItem = () => {
    if (!selectedOrder || !selectedOrder.items || selectedOrder.items.length === 0) {
      return;
    }
    
    // Use the first product as default
    const firstItem = selectedOrder.items[0];
    const newItem = {
      id: `temp-${Date.now()}-${Math.random()}`,
      productId: firstItem.productId,
      productName: firstItem.product || 'Unknown Product',
      sku: firstItem.sku || '',
      quantity: 1,
      unitPrice: firstItem.unitPrice || 0,
      totalPrice: firstItem.unitPrice || 0,
    };
    
    setAdjustedLineItems([...adjustedLineItems, newItem]);
  };

  // Remove line item
  const handleRemoveLineItem = (itemId) => {
    setAdjustedLineItems(adjustedLineItems.filter(item => item.id !== itemId));
  };

  // Update line item
  const handleUpdateLineItem = (itemId, field, value) => {
    setAdjustedLineItems(adjustedLineItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updated.totalPrice = (updated.quantity || 0) * (updated.unitPrice || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  // Save line items
  const handleSaveLineItems = async () => {
    if (!selectedOrder) {
      return;
    }

    // Validate line items
    if (adjustedLineItems.length === 0) {
      setLineItemsMessage({ severity: 'error', text: 'At least one line item is required.' });
      return;
    }

    // Check that quantities match original order (optional - we'll allow splitting)
    const totalQuantity = adjustedLineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const originalTotalQuantity = selectedOrder.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    if (totalQuantity !== originalTotalQuantity) {
      setLineItemsMessage({ 
        severity: 'warning', 
        text: `Total quantity (${totalQuantity}) doesn't match original (${originalTotalQuantity}). This will update the order quantities.` 
      });
    }

    try {
      setUpdatingLineItems(true);
      setLineItemsMessage(null);

      const lineItemsData = adjustedLineItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
      }));

      const shippingAmount = Number(selectedOrder.shippingAmount || 0);

      const response = await orderAPI.updateLineItems(selectedOrder.id, {
        lineItems: lineItemsData,
        shippingAmount: shippingAmount,
        notes: 'Line items adjusted manually'
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to update line items.');
      }

      // Update selected order first
      let updatedOrder = selectedOrder;
      if (response.data) {
        // Normalize items to ensure 'product' is a string name, not an object
        const normalizedItems = (response.data.items || []).map(item => ({
          ...item,
          product: typeof item.product === 'object' ? (item.product?.name || 'Unknown Product') : (item.product || 'Unknown Product'),
          sku: typeof item.product === 'object' ? (item.product?.sku || item.sku || '') : (item.sku || ''),
          // Ensure numbers are numbers
          quantity: Number(item.quantity),
          pickedQuantity: Number(item.pickedQuantity || 0),
          unitPrice: Number(item.unitPrice),
          totalPrice: Number(item.totalPrice)
        }));

        updatedOrder = {
          ...selectedOrder,
          items: normalizedItems,
          subtotal: Number(response.data.subtotal || adjustedLineItems.reduce((sum, item) => sum + item.totalPrice, 0)),
          shippingAmount: 0,
          taxAmount: 0,
          totalAmount: Number(response.data.subtotal || adjustedLineItems.reduce((sum, item) => sum + item.totalPrice, 0)),
        };
        setSelectedOrder(updatedOrder);
      }

      // Automatically update Hyfin invoice with new line items
      try {
        const newSubtotal = adjustedLineItems.reduce((sum, item) => sum + item.totalPrice, 0);
        const taxAmount = 0; // Force 0 as per requirement
        const newTotal = newSubtotal; // No tax, no shipping

        const invoicePayload = {
          overrideTotal: newTotal,
          reason: 'Line items adjusted - invoice updated automatically'
        };

        const invoiceResponse = await paymentAPI.updateInvoiceTotals(selectedOrder.id, invoicePayload);
        
        if (invoiceResponse.success) {
          setLineItemsMessage({
            severity: 'success',
            text: 'Line items and Hyfin invoice updated successfully.'
          });

          // Update order with invoice data
          if (invoiceResponse.data) {
            updatedOrder = {
              ...updatedOrder,
              totalAmount: invoiceResponse.data.totalAmount || newTotal,
              hyfinInvoiceId: invoiceResponse.data.hyfinInvoiceId || updatedOrder.hyfinInvoiceId,
              hyfinPaymentLink: invoiceResponse.data.hyfinPaymentLink || updatedOrder.hyfinPaymentLink,
            };
            setSelectedOrder(updatedOrder);
          }
        } else {
          setLineItemsMessage({
            severity: 'warning',
            text: 'Line items updated, but Hyfin invoice update failed. Please update manually using "Update Hyfin Invoice" button.'
          });
        }
      } catch (invoiceError) {
        console.error('Failed to update Hyfin invoice:', invoiceError);
        setLineItemsMessage({
          severity: 'warning',
          text: 'Line items updated, but Hyfin invoice update failed. Please update manually using "Update Hyfin Invoice" button.'
        });
      }

      // Refresh orders to get updated data
      await fetchOrders();

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseLineItemsDialog();
      }, 2000);
    } catch (error) {
      setLineItemsMessage({
        severity: 'error',
        text: error.message || 'Failed to update line items.'
      });
    } finally {
      setUpdatingLineItems(false);
    }
  };

  const derivedSelectedRateId = useMemo(() => {
    if (selectedRateId) return selectedRateId;

    const matchedOption = computedRateOptions.find(option =>
      normalizeCarrierCode(option.carrierCode) === normalizeCarrierCode(selectedCarrierCode) &&
      normalizeServiceCode(option.serviceCode) === normalizeServiceCode(selectedServiceCode)
    );

    if (matchedOption) {
      return matchedOption.id;
    }

    return computedRateOptions.length ? computedRateOptions[0].id : '';
  }, [selectedRateId, computedRateOptions, selectedCarrierCode, selectedServiceCode]);

  const selectedRate = useMemo(() => {
    return computedRateOptions.find(option => option.id === derivedSelectedRateId) || null;
  }, [computedRateOptions, derivedSelectedRateId]);

  const processSkuSelection = useCallback(async (rawSku) => {
    const sku = (rawSku || '').trim();
    if (!sku) {
      return;
    }

    if (!selectedOrder) {
      setScanMessage('❌ No order selected for picking.');
      setScanSeverity('error');
      return;
    }

    console.log('🔍 Processing Tissue ID:', sku);
    console.log('🔍 Current inventory state:', inventory);
    setPickingSku(sku);

    try {
      const inventoryItem = inventory.find(inv => (inv.tissueId === sku) || (inv.sku === sku));
      console.log('🔍 Found inventory item:', inventoryItem);
      
      if (!inventoryItem) {
        setScanMessage(`❌ Invalid tissue ID: "${sku}" not found in inventory. Please check Inventory Management.`);
        setScanSeverity('error');
        return;
      }

      if (inventoryItem.qcStatus && !['APPROVED', 'COMPLETED'].includes(inventoryItem.qcStatus)) {
        setScanMessage(`❌ Tissue ID ${sku} is not QC APPROVED (status: ${inventoryItem.qcStatus}). Only approved inventory can be assigned.`);
        setScanSeverity('error');
        return;
      }

      if (inventoryItem.status && inventoryItem.status !== 'AVAILABLE') {
        setScanMessage(`❌ Tissue ID ${sku} is ${inventoryItem.status}${inventoryItem.orderNumber ? ` (Order: ${inventoryItem.orderNumber})` : ''}. Cannot pick.`);
        setScanSeverity('error');
        return;
      }

      const orderItem = selectedOrder.items.find(item => 
        item.productId === inventoryItem.productId || 
        item.sku === inventoryItem.productId ||
        (inventoryItem.productName && item.product?.toLowerCase().includes(inventoryItem.productName.toLowerCase()))
      );
      
      if (!orderItem) {
        setScanMessage(`❌ Tissue ID ${sku} is for "${inventoryItem.productName}" which is not in this order.`);
        setScanSeverity('error');
        return;
      }

      if ((orderItem.pickedQuantity || 0) >= (orderItem.quantity || 0)) {
        setScanMessage(`⚠️ ${orderItem.product} already fully picked (${orderItem.quantity}/${orderItem.quantity})`);
        setScanSeverity('warning');
        return;
      }

      const realInventoryItem = inventory.find(inv => (inv.tissueId === sku) || (inv.sku === sku));
      
      if (!realInventoryItem) {
        setScanMessage(`❌ Tissue ID ${sku} not found in inventory`);
        setScanSeverity('error');
        return;
      }

      try {
        console.log('🔍 API Request data:', {
          orderItemId: orderItem.id,
          inventoryId: realInventoryItem.inventoryId,
          quantity: 1,
          batchId: realInventoryItem.batchId,
          lotNumber: realInventoryItem.lotNumber,
          notes: `Picked tissue ID: ${sku}`
        });
        
        const pickResponse = await fetch(`${API_BASE_URL}/orders/${selectedOrder.id}/pick-item`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'X-Client-Version': '2.0.0',
            'X-HIPAA-Compliant': 'true'
          },
          body: JSON.stringify({
            orderItemId: orderItem.id,
            inventoryId: realInventoryItem.inventoryId,
            quantity: 1,
            batchId: realInventoryItem.batchId,
            lotNumber: realInventoryItem.lotNumber,
            notes: `Picked tissue ID: ${sku}`
          })
        });

        if (!pickResponse.ok) {
          const errorData = await pickResponse.json();
          throw new Error(errorData.message || 'Failed to save pick to database');
        }

        const pickResult = await pickResponse.json();
        console.log('✅ Pick saved to database:', pickResult);

        const updatedItems = selectedOrder.items.map(item => {
          if (item.id === orderItem.id) {
            return { 
              ...item, 
              pickedQuantity: (item.pickedQuantity || 0) + 1,
              pickedSKUs: [...(item.pickedSKUs || []), sku],
              pickedTissueIds: [...(item.pickedTissueIds || []), sku]
            };
          }
          return item;
        });

        const updatedInventory = inventory.map(inv => {
          if ((inv.tissueId === sku) || (inv.sku === sku)) {
            return { 
              ...inv, 
              available: Math.max(0, (inv.available || 1) - 1),
              allocated: (inv.allocated || 0) + 1
            };
          }
          return inv;
        });

        const updatedOrder = { ...selectedOrder, items: updatedItems };
        setInventory(updatedInventory);
        setSelectedOrder(updatedOrder);
        
        console.log('📦 Picked item:', {
          product: orderItem.product,
          newPickedQty: (orderItem.pickedQuantity || 0) + 1,
          totalQty: orderItem.quantity,
          progress: getPickingProgress(updatedOrder),
          updatedOrderItems: updatedOrder.items.map(i => ({
            product: i.product,
            qty: i.quantity,
            picked: i.pickedQuantity
          }))
        });
        
        setScanMessage(`✓ Picked 1x ${orderItem.product} (${(orderItem.pickedQuantity || 0) + 1}/${orderItem.quantity}) | Tissue ID: ${sku} | Batch: ${realInventoryItem.batchId}`);
        setScanSeverity('success');

        const allPicked = updatedItems.every(item => (item.pickedQuantity || 0) >= (item.quantity || 0));
        if (allPicked) {
          setTimeout(() => {
            setCurrentStep(2);
            setScanMessage('');
          }, 1500);
        }

      } catch (apiError) {
        console.error('❌ Failed to save pick to database:', apiError);
        setScanMessage(`❌ Failed to save pick: ${apiError.message}`);
        setScanSeverity('error');
        return;
      }
    } finally {
      setPickingSku('');
      setScannedSKU('');
    }
  }, [inventory, selectedOrder]);

  const handleSKUScan = async (e) => {
    if (e.key === 'Enter' && scannedSKU) {
      e.preventDefault?.();
      await processSkuSelection(scannedSKU);
    }
  };

  const handleChecklistSelect = async (inventoryItem, checked) => {
    if (!checked) return;
    const sku = (inventoryItem?.tissueId || inventoryItem?.sku || '').trim();
    if (!sku || pickingSku === sku) return;
    await processSkuSelection(sku);
  };

  const handleCompletePacking = () => {
    const parsedWeight = parseFloat(packageWeight);
    if (!selectedPackageOption || Number.isNaN(parsedWeight) || parsedWeight <= 0) {
      return;
    }

    // Log packing for each item in FDA audit trail
    selectedOrder.items.forEach(item => {
      if (item.pickedQuantity > 0) {
        inventoryService.packInventory(
          item.sku,
          selectedOrder.orderNumber,
          'Demo User' // Replace with actual user from auth
        );
      }
    });

    const updatedOrder = {
      ...selectedOrder,
      packingCompletedAt: new Date().toISOString(),
      packingCompletedBy: 'Demo User',
      status: 'PACKED',
      carrier: selectedCarrierCode || selectedOrder.carrier,
      shippingMethod: selectedServiceCode || selectedOrder.shippingMethod,
      shipDateOverride: selectedShipDate,
      packageWeightLbs: parsedWeight,
      packageSize: selectedPackage,
      packageDimensions: {
        length: selectedPackageOption.length,
        width: selectedPackageOption.width,
        height: selectedPackageOption.height,
        units: 'inches'
      }
    };
    setSelectedOrder(updatedOrder);
    setRequestedCarrierCode(selectedCarrierCode || requestedCarrierCode);
    setRequestedServiceCode(selectedServiceCode || requestedServiceCode);
    setShippingStatusMessage('');
    setCurrentStep(3);
  };

  const handleGenerateLabel = async () => {
    if (!selectedOrder) {
      return;
    }

    if (!selectedCarrierCode || !selectedServiceCode) {
      setShippingStatusMessage('Please select a carrier option in the Pack step before generating a label.');
      setShippingStatusSeverity('error');
      setCurrentStep(2);
      return;
    }

    const shipDate = selectedShipDate || selectedOrder.shipDateOverride || new Date().toISOString().split('T')[0];

    const weightValue = (() => {
      const parsed = parseFloat(packageWeight);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
      if (!Number.isNaN(selectedOrder?.packageWeightLbs) && selectedOrder?.packageWeightLbs > 0) {
        return selectedOrder.packageWeightLbs;
      }
      return null;
    })();

    if (!weightValue) {
      setShippingStatusMessage('Enter a valid package weight (lbs) in the Pack step before creating the label.');
      setShippingStatusSeverity('error');
      setCurrentStep(2);
      return;
    }

    const dimensionsPayload = effectivePackageOption
      ? {
          length: effectivePackageOption.length,
          width: effectivePackageOption.width,
          height: effectivePackageOption.height,
          units: 'inches'
        }
      : selectedOrder?.packageDimensions
        ? {
            length: selectedOrder.packageDimensions.length,
            width: selectedOrder.packageDimensions.width,
            height: selectedOrder.packageDimensions.height,
            units: selectedOrder.packageDimensions.units || 'inches'
          }
        : undefined;

    try {
      setShippingStatusMessage('Creating shipping label with ShipStation…');
      setShippingStatusSeverity('info');

      if (selectedOrder.status !== 'PACKED') {
        const packResponse = await orderAPI.updateOrderStatus(selectedOrder.id, 'PACKED');
        if (!packResponse.success) {
          const errorMsg = packResponse.message || 'Failed to update order status to PACKED';
          setErrorDialogTitle('Order Status Error');
          setErrorDialogMessage('Unable to update order status');
          setErrorDialogDetails(errorMsg);
          setErrorDialogOpen(true);
          setShippingStatusMessage(`Failed to update order status: ${errorMsg}`);
          setShippingStatusSeverity('error');
          return;
        }

        setSelectedOrder(prev => (prev ? { ...prev, status: 'PACKED' } : prev));
      }

      console.log('--- handleGenerateLabel ---', {
        orderId: selectedOrder.id,
        carrierCode: selectedCarrierCode,
        serviceCode: selectedServiceCode,
        shipDate
      });

      const response = await shipstationAPI.createLabelForOrder(
        selectedOrder.id,
        selectedCarrierCode,
        selectedServiceCode,
        shipDate,
        {
          weight: {
            value: Number(weightValue.toFixed(2)),
            units: 'pounds'
          },
          dimensions: dimensionsPayload
        }
      );

      if (response.success && response.data) {
        const { trackingNumber, shipmentCost, labelData: receivedLabelData } = response.data;

        setTrackingNumber(trackingNumber);
        setLabelData(receivedLabelData);
        setSelectedShipDate(shipDate);

        const updatedOrder = {
          ...selectedOrder,
          trackingNumber,
          carrier: selectedCarrierCode,
          shippingMethod: selectedServiceCode,
          shippingLabelGeneratedAt: new Date().toISOString(),
          status: 'SHIPPED',
          shippedAt: new Date().toISOString(),
          actualShippingCost: shipmentCost,
          shipDateOverride: shipDate,
          packageWeightLbs: weightValue,
          packageSize: selectedPackage || selectedOrder.packageSize,
          packageDimensions: dimensionsPayload
        };

        setSelectedOrder(updatedOrder);
        setRequestedCarrierCode(selectedCarrierCode);
        setRequestedServiceCode(selectedServiceCode);
        setShippingStatusMessage('Shipping label created successfully. Download and print the label below.');
        setShippingStatusSeverity('success');

        setCurrentStep(4);
      } else {
        const errorMsg = response.message || 'Failed to create shipping label';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error creating shipping label:', error);
      const errorMessage = error.message || '';

      const isInsufficientBalance =
        errorMessage.toLowerCase().includes('insufficient') ||
        errorMessage.toLowerCase().includes('account balance') ||
        errorMessage.toLowerCase().includes('funds') ||
        errorMessage.toLowerCase().includes('balance');

      if (isInsufficientBalance) {
        setErrorDialogTitle('Insufficient ShipStation Account Balance');
        setErrorDialogMessage('Your ShipStation account does not have sufficient funds to create this shipping label.');
        setErrorDialogDetails(
          'To continue, please add funds to your ShipStation account. ' +
          'Once your account balance is sufficient, you can generate the shipping label again. ' +
          'For FedEx Wallet carriers, you may also need to ensure your carrier account is properly configured.'
        );
        setErrorDialogOpen(true);
      } else {
        setErrorDialogTitle('Label Generation Failed');
        setErrorDialogMessage('Unable to generate shipping label');
        setErrorDialogDetails(
          errorMessage || 'An unexpected error occurred while creating the shipping label. Please try again or contact support if the issue persists.'
        );
        setErrorDialogOpen(true);
      }

      setShippingStatusMessage(`Failed to create shipping label: ${errorMessage}`);
      setShippingStatusSeverity('error');
    }
  };

  const handlePrintLabel = () => {
    if (!labelData) {
      setScanMessage('❌ No label data available. Please generate the label first.');
      setScanSeverity('error');
      return;
    }

    try {
      // Convert base64 to blob
      const byteCharacters = atob(labelData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create URLs for download and print
      const downloadUrl = URL.createObjectURL(blob);
      const printUrl = URL.createObjectURL(blob);

      // Download the file
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `shipping-label-${selectedOrder?.orderNumber || 'label'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Open in new window for printing
      const printWindow = window.open();
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>Shipping Label - ${selectedOrder?.orderNumber || 'Label'}</title></head>
            <body style="margin: 0;">
              <embed src="${printUrl}" type="application/pdf" width="100%" height="100%" />
            </body>
          </html>
        `);
        printWindow.document.close();
        
        // Wait for PDF to load then trigger print dialog
        setTimeout(() => {
          printWindow.print();
          // Revoke URL after printing
          setTimeout(() => URL.revokeObjectURL(printUrl), 1000);
        }, 500);
      } else {
        // If popup blocked, just revoke the URL
        URL.revokeObjectURL(printUrl);
      }

      setScanMessage('✅ Label downloaded! Print dialog should open automatically.');
      setScanSeverity('success');
    } catch (error) {
      console.error('Error printing label:', error);
      setScanMessage(`❌ Error printing label: ${error.message}`);
      setScanSeverity('error');
    }
  };

  const handleCompleteOrder = async () => {
    try {
      setScanMessage('Marking order as shipped...');
      setScanSeverity('info');

      // Update order status to SHIPPED via API
      const response = await orderAPI.updateOrderStatus(selectedOrder.id, 'SHIPPED');
      if (!response.success) {
        const errorMsg = response.message || 'Failed to update order status to SHIPPED';
        setScanMessage(`❌ ${errorMsg}`);
        setScanSeverity('error');
        return;
      }

      const updatedOrder = {
        ...selectedOrder,
        completedAt: new Date().toISOString(),
        completedBy: 'current-user-id',
        status: 'SHIPPED'
      };

      // Update orders list
      const updatedOrders = orders.map(o =>
        o.id === selectedOrder.id ? updatedOrder : o
      );
      setOrders(updatedOrders);

      setScanMessage('');
      setScanSeverity('success');
      setCompletedOrderInfo({
        orderNumber: updatedOrder.orderNumber,
        totalAmount: updatedOrder.totalAmount,
        trackingNumber: trackingNumber || updatedOrder.trackingNumber
      });
      setCompletionDialogOpen(true);
    } catch (error) {
      console.error('Error completing order:', error);
      setScanMessage(`❌ Failed to complete order: ${error.message}`);
      setScanSeverity('error');
    }
  };

  const handleCompletionDialogClose = () => {
    setCompletionDialogOpen(false);
    setProcessDialogOpen(false);
    setSelectedOrder(null);
    setCurrentStep(0);
    setScanMessage('');
    setTrackingNumber('');
    setLabelData(null);
    setCompletedOrderInfo(null);
    setAvailableRates([]);
    setSelectedRateId('');
    setRatesError('');
    setSelectedCarrierCode('');
    setSelectedServiceCode('');
    setSelectedShipDate(new Date().toISOString().split('T')[0]);
    setRequestedCarrierCode(null);
    setRequestedServiceCode(null);
    setRequestedDeliveryDate(null);
    setShippingStatusMessage('');
    setShippingStatusSeverity('info');
    setSelectedPackage('');
    setPackageWeight('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return '#f59e0b';
      case 'ASSIGNED': return '#3b82f6';
      case 'PICKING': return '#8b5cf6';
      case 'PACKED': return '#06b6d4';
      case 'SHIPPED': return '#10b981';
      case 'DELIVERED': return '#22c55e';
      case 'CANCELLED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return '#ef4444';
      case 'HIGH': return '#f59e0b';
      case 'NORMAL': return '#3b82f6';
      case 'LOW': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPickingProgress = (order) => {
    if (!order || !order.items || order.items.length === 0) return 0;
    const totalItems = order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const pickedItems = order.items.reduce((sum, item) => sum + (item.pickedQuantity || 0), 0);
    if (totalItems === 0) return 0;
    const progress = Math.round((pickedItems / totalItems) * 100);
    return progress;
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    return Number.isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
  };
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
  const formatDateTime = (dateString) => new Date(dateString).toLocaleString();

  // Filter orders by status for different tabs
  const activeOrders = orders.filter(o => ['PENDING', 'ASSIGNED', 'PICKING', 'PACKED'].includes(o.status));
  const inTransitOrders = orders.filter(o => o.status === 'SHIPPED');
  const completedOrders = orders.filter(o => o.status === 'DELIVERED');

  // Get current tab orders
  const currentTabOrders = activeTab === 0 ? activeOrders :
    activeTab === 1 ? inTransitOrders :
      completedOrders;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#039582' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Order Processing
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Enterprise-grade order fulfillment workflow
            </Typography>
          </Box>
          {orgUpper === 'EPL' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateOrder}
              sx={{
                bgcolor: 'white',
                color: '#039582',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: '#027A6B'
                }
              }}
            >
              Create Order
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Development Mode: Test Error Dialogs */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#fef3c7', borderRadius: 2, border: '2px dashed #f59e0b' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#92400e' }}>
            🧪 Development Mode: Test Error Dialogs
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => window.testInsufficientBalanceDialog()}
              sx={{
                borderColor: '#ef4444',
                color: '#ef4444',
                '&:hover': { 
                  borderColor: '#dc2626',
                  bgcolor: '#fef2f2'
                },
                textTransform: 'none'
              }}
            >
              Test: Insufficient Balance Dialog
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.testGenericErrorDialog()}
              sx={{
                borderColor: '#6b7280',
                color: '#6b7280',
                '&:hover': { 
                  borderColor: '#4b5563',
                  bgcolor: '#f9fafb'
                },
                textTransform: 'none'
              }}
            >
              Test: Generic Error Dialog
            </Button>
          </Box>
        </Box>
      )}

      {/* Tabs for different order queues */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 3,
          bgcolor: 'white',
          borderRadius: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem'
          },
          '& .Mui-selected': {
            color: '#039582'
          },
          '& .MuiTabs-indicator': {
            bgcolor: '#039582'
          }
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Active Queue
              <Chip label={activeOrders.length} size="small" sx={{ bgcolor: '#f59e0b', color: 'white' }} />
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              In Transit
              <Chip label={inTransitOrders.length} size="small" sx={{ bgcolor: '#3b82f6', color: 'white' }} />
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Completed
              <Chip label={completedOrders.length} size="small" sx={{ bgcolor: '#10b981', color: 'white' }} />
            </Box>
          }
        />
      </Tabs>

      {/* Order Queue */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {activeTab === 0 ? 'Active Orders' : activeTab === 1 ? 'Orders In Transit' : 'Completed Orders'}
            </Typography>
            <Chip
              label={`${currentTabOrders.length} Orders`}
              sx={{ bgcolor: '#039582', color: 'white', fontWeight: 600 }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={1.5} sx={{ p: 1 }}>
              {currentTabOrders.map((order) => (
                <Paper key={order.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#039582' }}>
                      {order.orderNumber}
                    </Typography>
                    <Chip label={order.status} size="small" sx={{ bgcolor: getStatusColor(order.status), color: 'white', fontWeight: 600 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{order.client}</Typography>
                    <Typography variant="body2" color="text.secondary">{formatDate(order.orderDate)}</Typography>
                    <Typography variant="body2" color="text.secondary">Items: {order.items.length}</Typography>
                    <Typography variant="body2" color="text.secondary">Total: {formatCurrency(order.totalAmount)}</Typography>
                    <Chip label={order.priority} size="small" sx={{ bgcolor: getPriorityColor(order.priority), color: 'white', fontWeight: 600 }} />
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {activeTab === 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<StartIcon />}
                        onClick={() => handleStartProcessing(order)}
                        sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' }, textTransform: 'none' }}
                      >
                        {order.status === 'PENDING' ? 'Start' : 'Continue'}
                      </Button>
                    )}
                    {activeTab === 1 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ShipIcon />}
                        disabled={!order.trackingNumber}
                        onClick={() => {
                          const fallbackCarrier = extractShippingMethod(order.notes || selectedOrder?.notes || '').carrier;
                          const trackingUrl = getCarrierTrackingUrl(order.carrier || fallbackCarrier, order.trackingNumber);
                          if (trackingUrl) {
                            window.open(trackingUrl, '_blank');
                          }
                        }}
                        sx={{ borderColor: '#3b82f6', color: '#3b82f6', textTransform: 'none' }}
                      >
                        Track
                      </Button>
                    )}
                    {activeTab === 2 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={() => alert('View completed order details (TODO)')}
                        sx={{ borderColor: '#10b981', color: '#10b981', textTransform: 'none' }}
                      >
                        View
                      </Button>
                    )}
                    <Typography variant="body2" sx={{ ml: 'auto' }}>
                      {order.assignedTo || 'Unassigned'}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expected Delivery</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTabOrders.map((order) => (
                    <TableRow key={order.id} sx={{ '&:hover': { bgcolor: '#f7f7f8' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#039582' }}>
                          {order.orderNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.client}</TableCell>
                      <TableCell>{formatDate(order.orderDate)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          color: order.expectedDeliveryDate ? '#ef4444' : '#6b7280',
                          fontWeight: order.expectedDeliveryDate ? 600 : 400
                        }}>
                          {order.expectedDeliveryDate || 'Not specified'}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.items.length}</TableCell>
                      <TableCell>{formatCurrency(order.totalAmount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.priority}
                          size="small"
                          sx={{ bgcolor: getPriorityColor(order.priority), color: 'white', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          size="small"
                          sx={{ bgcolor: getStatusColor(order.status), color: 'white', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.assignedTo || 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {activeTab === 0 && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<StartIcon />}
                            onClick={() => handleStartProcessing(order)}
                            sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' }, textTransform: 'none' }}
                          >
                            {order.status === 'PENDING' ? 'Start' : 'Continue'}
                          </Button>
                        )}
                        {activeTab === 1 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ShipIcon />}
                            disabled={!order.trackingNumber}
                            onClick={() => {
                              const fallbackCarrier = extractShippingMethod(order.notes || selectedOrder?.notes || '').carrier;
                              const trackingUrl = getCarrierTrackingUrl(order.carrier || fallbackCarrier, order.trackingNumber);
                              if (trackingUrl) {
                                window.open(trackingUrl, '_blank');
                              }
                            }}
                            sx={{ borderColor: '#3b82f6', color: '#3b82f6', textTransform: 'none' }}
                          >
                            Track
                          </Button>
                        )}
                        {activeTab === 2 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon />}
                            onClick={() => alert('View completed order details (TODO)')}
                            sx={{ borderColor: '#10b981', color: '#10b981', textTransform: 'none' }}
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog (EPL Admins) */}
      <Dialog
        open={createOrderOpen}
        onClose={handleCloseCreateOrder}
        maxWidth="md"
        fullWidth
        keepMounted={false}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {createOrderSuccess ? 'Order Created Successfully' : 'Create New Order'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          {createOrderSuccess ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                The order <strong>{createOrderSuccess.orderNumber}</strong> has been created and added to the processing queue.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You can jump directly into the fulfillment workflow or create another order for a different client.
              </Typography>
            </Box>
          ) : (
            <>
              <Stepper activeStep={createOrderStep} orientation="vertical">
                <Step>
                  <StepLabel>Select Customer</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Choose the clinic or client placing this order. Shipping details will prefll from their profile.
                    </Typography>
                    <Autocomplete
                      options={availableClients}
                      value={selectedClient}
                      onChange={(event, value) => handleClientChange(value)}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        const name = formatFullName(option);
                        const clinic = option.profile?.clinicName;
                        return clinic ? `${clinic} (${name || option.email})` : name || option.email;
                      }}
                      loading={clientsLoading}
                      noOptionsText={clientsLoading ? 'Loading clients...' : 'No clients found'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search client or clinic"
                          placeholder="Start typing clinic or client name"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {option.profile?.clinicName || formatFullName(option) || option.email}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.email}
                                {option.profile?.city ? ` • ${option.profile.city}, ${option.profile.state || ''}` : ''}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Add Products</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add the items requested by the client. Quantities can be adjusted per SKU.
                    </Typography>
                    <Stack spacing={2}>
                      {isSingleProductMode ? (
                        <>
                          {orderItems.map((item) => (
                            <Card key={item.id} variant="outlined">
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {availableProducts[0]?.name || 'Product'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  SKU: {availableProducts[0]?.sku || '—'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ${Number(availableProducts[0]?.basePrice || 0).toFixed(2)} per unit
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                  type="number"
                                  label="Quantity"
                                  value={item.quantity}
                                  onChange={(event) => updateOrderItemQuantity(item.id, event.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{ maxWidth: 120 }}
                                />
                                <TextField
                                  type="number"
                                  label="Unit Price"
                                  value={item.unitPrice}
                                  onChange={(event) => updateOrderItemPrice(item.id, event.target.value)}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                  }}
                                  sx={{ maxWidth: 140 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, flex: 1 }}>
                                  Total: ${(Number(item.unitPrice || 0) * item.quantity).toFixed(2)}
                                </Typography>
                                <Button
                                  color="error"
                                  onClick={() => removeOrderItem(item.id)}
                                  disabled={orderItems.length === 1}
                                  sx={{ textTransform: 'none' }}
                                >
                                  Remove
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                          ))}
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addOrderItem}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                          >
                            Add Line Item
                          </Button>
                        </>
                      ) : (
                        <>
                          {orderItems.map((item) => (
                            <Card key={item.id} variant="outlined">
                              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                  options={availableProducts}
                                  value={item.product}
                                  onChange={(event, value) => updateOrderItemProduct(item.id, value)}
                                  getOptionLabel={(option) => option ? `${option.name} (${option.sku})` : ''}
                                  loading={productsLoading}
                                  noOptionsText={productsLoading ? 'Loading products...' : 'No products found'}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select product"
                                      placeholder="Search products by name or SKU"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        )
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                      <li key={key} {...optionProps}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {option.name} ({option.sku})
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            ${Number(option.basePrice || 0).toFixed(2)} • {option.category || '—'}
                                          </Typography>
                                        </Box>
                                      </li>
                                    );
                                  }}
                                />
                                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                      type="number"
                                      label="Quantity"
                                      value={item.quantity}
                                      onChange={(event) => updateOrderItemQuantity(item.id, event.target.value)}
                                      inputProps={{ min: 1 }}
                                      sx={{ maxWidth: 120 }}
                                    />
                                    <TextField
                                      type="number"
                                      label="Unit Price"
                                      value={item.unitPrice}
                                      onChange={(event) => updateOrderItemPrice(item.id, event.target.value)}
                                      inputProps={{ min: 0, step: 0.01 }}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                      }}
                                      sx={{ maxWidth: 140 }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      {item.product && (
                                        <Typography variant="body2" color="text.secondary">
                                          Total: ${(Number(item.unitPrice || 0) * item.quantity).toFixed(2)} • Category: {item.product.category || '—'}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Button
                                      color="error"
                                      onClick={() => removeOrderItem(item.id)}
                                    disabled={orderItems.length === 1}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addOrderItem}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                          >
                            Add Another Product
                          </Button>
                        </>
                      )}
                    </Stack>
                  </StepContent>
                </Step>

                <Step>
                  <StepLabel>Shipping & Review</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Confirm shipping details and review the order summary. Address fields can be adjusted before submission.
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Recipient Name"
                          fullWidth
                          value={shippingDetails.name}
                          onChange={handleShippingFieldChange('name')}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Company / Clinic"
                          fullWidth
                          value={shippingDetails.company}
                          onChange={handleShippingFieldChange('company')}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Address Line 1"
                          fullWidth
                          value={shippingDetails.address1}
                          onChange={handleShippingFieldChange('address1')}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Address Line 2"
                          fullWidth
                          value={shippingDetails.address2}
                          onChange={handleShippingFieldChange('address2')}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="City"
                          fullWidth
                          value={shippingDetails.city}
                          onChange={handleShippingFieldChange('city')}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>State</InputLabel>
                          <Select
                            value={shippingDetails.state}
                            label="State"
                            onChange={handleShippingFieldChange('state')}
                            required
                          >
                            {US_STATES.map((state) => (
                              <MenuItem key={state.value} value={state.value}>
                                {state.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="ZIP Code"
                          fullWidth
                          value={shippingDetails.zipCode}
                          onChange={handleShippingFieldChange('zipCode')}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Contact Phone"
                          fullWidth
                          value={shippingDetails.phone}
                          onChange={handleShippingFieldChange('phone')}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Order Notes (Optional)"
                          fullWidth
                          value={orderNotes}
                          onChange={(event) => setOrderNotes(event.target.value)}
                          placeholder="Delivery instructions, payment terms, etc."
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                      <TextField
                        type="date"
                        label="Requested Delivery Date"
                        value={deliveryDate}
                        onChange={(event) => setDeliveryDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: getDefaultDeliveryDate() }}
                        required
                        error={Boolean(createOrderError && !deliveryDate)}
                        helperText={createOrderError && !deliveryDate ? 'Delivery date is required' : 'Select the arrival date requested by the clinic'}
                        sx={{ maxWidth: { xs: '100%', md: 260 } }}
                      />
                      <FormControl component="fieldset" sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Shipping Method
                        </Typography>
                        <RadioGroup
                          value={shippingMethod}
                          onChange={(event) => setShippingMethod(event.target.value)}
                        >
                          {SHIPPING_METHOD_OPTIONS.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {option.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>

                    <Box sx={{ bgcolor: '#f7f7f8', borderRadius: 1, p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Order Summary
                      </Typography>
                      {orderItems.filter((item) => item.productId && item.product).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Add at least one product to see the summary.
                        </Typography>
                      ) : (
                        <>
                          <List dense>
                            {orderItems
                              .filter((item) => item.productId && item.product)
                              .map((item) => (
                                <ListItem key={item.id} sx={{ px: 0 }}>
                                  <ListItemText
                                    primary={`${item.quantity} × ${item.product.name}`}
                                    secondary={`SKU: ${item.product.sku} • $${Number(item.unitPrice || 0).toFixed(2)} each`}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    ${ (Number(item.unitPrice || 0) * item.quantity).toFixed(2) }
                                  </Typography>
                                </ListItem>
                              ))}
                          </List>
                          <Divider sx={{ my: 1 }} />
                          {(() => {
                            const subtotal = orderItems
                              .filter((item) => item.productId && item.product)
                              .reduce((sum, item) => sum + Number(item.unitPrice || 0) * item.quantity, 0);
                            const total = subtotal;
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2">Requested Delivery: {deliveryDate ? new Date(deliveryDate).toLocaleDateString() : '—'}</Typography>
                                <Typography variant="body2">Shipping Method: {getShippingMethodLabel(shippingMethod)}</Typography>
                                <Typography variant="body2">Subtotal: ${subtotal.toFixed(2)}</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
                                  Estimated Total: ${total.toFixed(2)}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>
              {createOrderError && (
                <Alert severity="error" sx={{ mt: 3 }} onClose={() => setCreateOrderError('')}>
                  {createOrderError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {createOrderSuccess ? (
            <>
              <Button onClick={handleCreateAnotherOrder} sx={{ textTransform: 'none' }}>
                Create Another Order
              </Button>
              <Button
                variant="contained"
                onClick={handleNavigateToOrderProcessing}
                sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' }, textTransform: 'none' }}
              >
                Go to Order Workflow
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseCreateOrder} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              {createOrderStep > 0 && (
                <Button onClick={handleBackCreateOrderStep} sx={{ textTransform: 'none' }}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={createOrderStep === ORDER_CREATION_STEPS.length - 1 ? handleSubmitOrder : handleNextCreateOrderStep}
                disabled={creatingOrder}
                sx={{
                  bgcolor: '#039582',
                  '&:hover': { bgcolor: '#027A6B' },
                  textTransform: 'none',
                  minWidth: 140
                }}
              >
                {createOrderStep === ORDER_CREATION_STEPS.length - 1
                  ? creatingOrder ? 'Creating...' : 'Create Order'
                  : 'Next'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Processing Dialog - Full Workflow */}
      <Dialog
        open={processDialogOpen}
        onClose={() => setProcessDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              Process Order: {selectedOrder?.orderNumber}
            </Typography>
            <IconButton onClick={() => setProcessDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Left: Order Details */}
              <Grid item xs={12} md={5}>
                <Card sx={{ bgcolor: '#f7f7f8', height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Order Details
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.client}</Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Shipping Address</Typography>
                      <Typography variant="body2">{selectedOrder.shippingAddress.name}</Typography>
                      <Typography variant="body2">{selectedOrder.shippingAddress.address1}</Typography>
                      <Typography variant="body2">
                        {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.zipCode}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Expected Delivery</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                        {selectedOrder.expectedDeliveryDate || 'Not specified'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Items to Pick</Typography>
                    {selectedOrder.items.map((item) => (
                      <Box key={item.id} sx={{
                        mb: 1,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #ececef'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.product}
                          </Typography>
                          <Chip
                            label={`${item.pickedQuantity || 0}/${item.quantity || 0}`}
                            size="small"
                            color={(item.pickedQuantity || 0) >= (item.quantity || 0) ? 'success' : 'default'}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Product SKU: {item.sku || '—'}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={((item.pickedQuantity || 0) / (item.quantity || 1)) * 100}
                          sx={{
                            mt: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': { bgcolor: '#039582' }
                          }}
                        />
                      </Box>
                    ))}

                    {selectedOrder.notes && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">Client Notes:</Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          "{selectedOrder.notes}"
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Right: Workflow Steps */}
              <Grid item xs={12} md={7}>
                <Stepper activeStep={currentStep} orientation="vertical">

                  {/* Step 0: Assignment */}
                  <Step>
                    <StepLabel>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Assign to User
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Order automatically assigned to you for audit compliance
                      </Alert>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Assigned At:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                          {formatDateTime(selectedOrder.assignedAt || new Date().toISOString())}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setCurrentStep(1);
                            const updated = { ...selectedOrder, pickingStartedAt: new Date().toISOString(), status: 'PICKING' };
                            setSelectedOrder(updated);
                          }}
                          sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                        >
                          Start Picking Items
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 1: Pick Items */}
                  <Step>
                    <StepLabel>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Pick Items (Scan or Select from Inventory)
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Scan the tissue ID barcode or click the checklist items below to assign inventory.
                      </Alert>
                      
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          inputRef={scanInputRef}
                          label="Scan or Enter Tissue ID"
                          value={scannedSKU}
                          onChange={(event) => setScannedSKU(event.target.value)}
                          onKeyDown={handleSKUScan}
                          placeholder="Scan barcode, then press Enter"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ScanIcon sx={{ color: '#039582' }} />
                              </InputAdornment>
                            )
                          }}
                          helperText="Press Enter after scanning. Checklist selections will automatically assign inventory."
                        />
                      </Box>

                      {scanMessage && (
                        <Alert severity={scanSeverity} sx={{ mb: 2 }} onClose={() => setScanMessage('')}>
                          {scanMessage}
                        </Alert>
                      )}

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Inventory Checklist
                        </Typography>
                        <Box
                          sx={{
                            maxHeight: 360,
                            overflowY: 'auto',
                            pr: 1,
                            border: '1px solid #ececef',
                            borderRadius: 1,
                            backgroundColor: '#ffffff',
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5f5', borderRadius: 3 }
                          }}
                        >
                          <Stack spacing={1.5} sx={{ py: 1, pr: 0.5 }}>
                            {selectedOrder.items.map((item) => {
                            const itemPickedSkus = [
                              ...(item.pickedSKUs || []),
                              ...(item.pickedTissueIds || [])
                            ];
                            const matchingInventory = inventory.filter((inv) => {
                              const tissueId = inv.tissueId || inv.sku;
                              const inventoryMatchesProduct =
                                inv.productId === item.productId ||
                                inv.baseSku === item.sku ||
                                (inv.productName &&
                                  item.product &&
                                  inv.productName.toLowerCase().includes(item.product.toLowerCase()));
                              const isAlreadyPicked = tissueId && itemPickedSkus.includes(tissueId);
                              return inventoryMatchesProduct && ((inv.available && inv.available > 0) || isAlreadyPicked);
                            });

                            return (
                              <Box
                                key={item.id}
                                sx={{
                                  p: 2,
                                  bgcolor: 'white',
                                  borderRadius: 1,
                                  border: '1px solid #ececef'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {item.product}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Required: {item.quantity} • Picked: {item.pickedQuantity || 0}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`${item.pickedQuantity || 0}/${item.quantity || 0}`}
                                    size="small"
                                    color={(item.pickedQuantity || 0) >= (item.quantity || 0) ? 'success' : 'default'}
                                  />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {matchingInventory.length > 0 ? (
                                    matchingInventory.map((inv) => {
                                      const tissueId = inv.tissueId || inv.sku;
                                      const isPicked = tissueId ? itemPickedSkus.includes(tissueId) : false;
                                      const expirationLabel = inv.expirationDate
                                        ? new Date(inv.expirationDate).toLocaleDateString()
                                        : null;

                                      return (
                                        <FormControlLabel
                                          key={tissueId || `${inv.inventoryId}-${inv.batchId}`}
                                          control={
                                            <Checkbox
                                              checked={isPicked}
                                              onChange={(event) => handleChecklistSelect(inv, event.target.checked)}
                                              disabled={isPicked || pickingSku === tissueId}
                                              sx={{
                                                '&.Mui-checked': { color: '#039582' }
                                              }}
                                            />
                                          }
                                          label={
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                {tissueId || 'Unknown Tissue ID'}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                Lot: {inv.lotNumber || '—'}
                                                {inv.location ? ` • Location: ${inv.location}` : ''}
                                                {expirationLabel ? ` • Expires: ${expirationLabel}` : ''}
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                      );
                                    })
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No available inventory matches this item.
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                            })}
                          </Stack>
                        </Box>
                      </Box>

                      <Box sx={{
                        p: 2,
                        bgcolor: '#f7f7f8',
                        borderRadius: 1,
                        border: '1px solid #ececef'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Picking Progress: {getPickingProgress(selectedOrder)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={getPickingProgress(selectedOrder)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': { bgcolor: '#039582' }
                          }}
                        />
                      </Box>

                      <Button
                        variant="contained"
                        onClick={() => {
                          const updated = { ...selectedOrder, pickingCompletedAt: new Date().toISOString() };
                          setSelectedOrder(updated);
                          setCurrentStep(2);
                          setScanMessage('');
                        }}
                        disabled={getPickingProgress(selectedOrder) < 100}
                        sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                      >
                        Complete Picking
                      </Button>
                    </StepContent>
                  </Step>

                  {/* Step 2: Pack Order */}
                  <Step>
                    <StepLabel>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Pack Order
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Verify all items are packed, review the client shipping request, and confirm the carrier option before generating a label.
                      </Alert>

                      <Box sx={{ mb: 2, p: 2, bgcolor: '#f7f7f8', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Packing Checklist:
                        </Typography>
                        {selectedOrder.items.map((item) => (
                          <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <SuccessIcon sx={{ fontSize: 16, color: '#10b981', mr: 1 }} />
                            <Typography variant="body2">
                              {item.quantity}x {item.product}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: '#f7f7f8',
                          borderRadius: 1,
                          border: '1px solid #ececef'
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Client Shipping Request
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Requested Delivery Date:{' '}
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {requestedDeliveryDate ? formatDate(requestedDeliveryDate) : 'Not provided'}
                          </Box>
                        </Typography>
                        <Typography variant="body2">
                          Preferred Carrier:{' '}
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {getCarrierDisplayName(requestedCarrierCode || selectedCarrierCode)}
                          </Box>
                        </Typography>
                        {requestedServiceCode && (
                          <Typography variant="body2">
                            Preferred Service:{' '}
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {formatServiceName(requestedServiceCode)}
                            </Box>
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 1,
                          border: '1px solid #ececef'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Carrier Rates & Ship Date
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => fetchCarrierRates(true)}
                            disabled={ratesLoading}
                            sx={{ color: '#039582' }}
                          >
                            {ratesLoading ? 'Refreshing…' : 'Refresh Rates'}
                          </Button>
                        </Box>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              label="Ship Date"
                              type="date"
                              value={selectedShipDate}
                              onChange={(event) => setSelectedShipDate(event.target.value)}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Select the carrier rate that will be used when generating the shipping label.
                            </Typography>
                          </Grid>
                        </Grid>

                        {ratesLoading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                            <CircularProgress size={24} sx={{ color: '#039582' }} />
                            <Typography variant="body2" color="text.secondary">
                              Fetching live rates from ShipStation…
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 2 }}>
                            {ratesError && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {ratesError}
                              </Alert>
                            )}
                            {!ratesError && availableRates.length === 0 && (
                              <Alert severity="warning" sx={{ mb: 2 }}>
                                Live rates unavailable. Choose the carrier manually using the options below.
                              </Alert>
                            )}
                            <RadioGroup
                              value={derivedSelectedRateId}
                              onChange={(event) => handleRateSelectionChange(event.target.value)}
                            >
                              {computedRateOptions.map((rate) => (
                                <FormControlLabel
                                  key={rate.id}
                                  value={rate.id}
                                  control={
                                    <Radio sx={{ color: '#039582', '&.Mui-checked': { color: '#027A6B' } }} />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {getCarrierDisplayName(rate.carrierFriendlyName || rate.carrierCode)} •{' '}
                                        {rate.serviceName || formatServiceName(rate.serviceCode)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {rate.shipmentCost != null
                                          ? `Rate: ${formatCurrency(rate.shipmentCost)}`
                                          : 'Rate unavailable'}
                                        {rate.estimatedDeliveryDate
                                          ? ` • Est Delivery: ${formatDate(rate.estimatedDeliveryDate)}`
                                          : ''}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </Box>
                        )}
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                          <FormControl component="fieldset" fullWidth>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Select Package Size:
                            </Typography>
                            <Select
                              value={selectedPackage}
                              onChange={(e) => setSelectedPackage(e.target.value)}
                              displayEmpty
                              fullWidth
                              sx={{ minWidth: 200 }}
                            >
                              <MenuItem value="" disabled>
                                Choose a box size...
                              </MenuItem>
                              {PACKAGE_SIZE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {selectedPackageOption && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Dimensions: {selectedPackageOption.length}" L × {selectedPackageOption.width}" W × {selectedPackageOption.height}" H
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Package Weight (lbs):
                          </Typography>
                          <TextField
                            type="number"
                            inputProps={{ min: 0, step: 0.1 }}
                            value={packageWeight}
                            onChange={(event) => setPackageWeight(event.target.value)}
                            placeholder="Enter total packed weight"
                            fullWidth
                          />
                          <Typography variant="caption" color="text.secondary">
                            Required for label generation. Include ice/packing materials.
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          sx={{
                            borderColor: '#039582',
                            color: '#039582',
                            '&:hover': { borderColor: '#027A6B', bgcolor: '#f0fdfa' }
                          }}
                        >
                          Print Packing Slip
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleCompletePacking}
                          disabled={
                            !selectedPackage ||
                            !selectedCarrierCode ||
                            !selectedShipDate ||
                            !packageWeight ||
                            Number.isNaN(parseFloat(packageWeight)) ||
                            parseFloat(packageWeight) <= 0
                          }
                          sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                        >
                          Confirm Packing & Continue to Invoice
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 3: Finalize Invoice */}
                  <Step>
                    <StepLabel>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Finalize Invoice
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Review and adjust line items before generating the invoice. Changes here will update the Hyfin invoice sent to the client.
                      </Alert>

                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: '#f7f7f8',
                          borderRadius: 1,
                          border: '1px solid #ececef'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Invoice Adjustments (Hyfin)
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenLineItemsDialog}
                            disabled={!selectedOrder || updatingInvoice}
                            startIcon={<AssignIcon />}
                            sx={{
                              bgcolor: '#039582',
                              '&:hover': { bgcolor: '#027A6B' }
                            }}
                          >
                            Adjust Line Items & Pricing
                          </Button>
                        </Box>

                        {invoiceUpdateMessage && (
                          <Alert
                            severity={invoiceUpdateMessage.severity}
                            sx={{ mb: 2 }}
                            onClose={() => setInvoiceUpdateMessage(null)}
                          >
                            {invoiceUpdateMessage.text}
                          </Alert>
                        )}

                        <FormControlLabel
                          control={
                            <Switch
                              checked={invoiceSample}
                              onChange={handleInvoiceSampleToggle}
                              color="primary"
                            />
                          }
                          label="Mark this order as a sample (no charge)"
                          sx={{ mb: 2 }}
                        />

                        {Boolean(selectedOrder) && invoiceHasPendingChanges && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                            Pending update — click "Update Hyfin Invoice" to apply changes.
                          </Typography>
                        )}

                        <TextField
                          label="Adjustment Notes"
                          placeholder="Reason for price change or sample designation (optional)"
                          value={invoiceAdjustmentNote}
                          onChange={(event) => setInvoiceAdjustmentNote(event.target.value)}
                          multiline
                          minRows={2}
                          fullWidth
                          disabled={updatingInvoice}
                          sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleHyfinInvoiceUpdate}
                            disabled={invoiceUpdateDisabled}
                            sx={{ 
                              borderColor: '#039582', 
                              color: '#039582',
                              '&:hover': { borderColor: '#027A6B', bgcolor: '#f0fdfa' } 
                            }}
                          >
                            {updatingInvoice ? 'Updating…' : 'Update Hyfin Invoice'}
                          </Button>
                        </Box>
                      </Box>

                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #ececef', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Current Invoice Summary
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              ${Number(selectedOrder?.subtotal || 0).toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Total:</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#039582' }}>
                              ${Number(selectedOrder?.totalAmount || 0).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={() => setCurrentStep(4)}
                        sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                      >
                        Confirm Invoice & Continue
                      </Button>
                    </StepContent>
                  </Step>

                  {/* Step 4: Generate Shipping Label */}
                  <Step>
                    <StepLabel>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Generate Shipping Label
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {shippingStatusMessage && (
                        <Alert
                          severity={shippingStatusSeverity}
                          sx={{ mb: 2 }}
                          onClose={() => setShippingStatusMessage('')}
                        >
                          {shippingStatusMessage}
                        </Alert>
                      )}

                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: '#f7f7f8',
                          borderRadius: 1,
                          border: '1px solid #ececef'
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Selected Shipping Option
                        </Typography>
                        {selectedCarrierCode ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {getCarrierDisplayName(selectedCarrierCode)} •{' '}
                              {selectedRate?.serviceName || formatServiceName(selectedServiceCode) || 'Service not selected'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Ship Date: {selectedOrder?.shipDateOverride || selectedShipDate || new Date().toISOString().split('T')[0]}
                            </Typography>
                            {(selectedRate?.shipmentCost != null || selectedOrder.actualShippingCost != null) && (
                              <Typography variant="body2" color="text.secondary">
                                Rate:{' '}
                                {selectedOrder.actualShippingCost != null
                                  ? formatCurrency(selectedOrder.actualShippingCost)
                                  : selectedRate?.shipmentCost != null
                                    ? formatCurrency(selectedRate.shipmentCost)
                                    : 'N/A'}
                              </Typography>
                            )}
                            {selectedRate?.estimatedDeliveryDate && (
                              <Typography variant="body2" color="text.secondary">
                                Est Delivery: {formatDate(selectedRate.estimatedDeliveryDate)}
                              </Typography>
                            )}
                            {effectivePackageOption && (
                              <Typography variant="body2" color="text.secondary">
                                Package: {effectivePackageOption.label} — {effectivePackageOption.length}" L × {effectivePackageOption.width}" W × {effectivePackageOption.height}" H
                              </Typography>
                            )}
                            {!effectivePackageOption && selectedOrder?.packageDimensions && (
                              <Typography variant="body2" color="text.secondary">
                                Package: {selectedOrder.packageDimensions.length}" L × {selectedOrder.packageDimensions.width}" W × {selectedOrder.packageDimensions.height}" H
                              </Typography>
                            )}
                            {displayPackageWeight && (
                              <Typography variant="body2" color="text.secondary">
                                Weight: {displayPackageWeight.toFixed(2)} lbs
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              Invoice Total: ${Number(selectedOrder?.totalAmount ?? 0).toFixed(2)}
                              {selectedOrder?.discountAmount
                                ? ` (Discount: $${Number(selectedOrder.discountAmount).toFixed(2)})`
                                : ''}
                            </Typography>
                            {selectedOrder?.isSample && (
                              <Typography variant="caption" color="error.main">
                                Marked as sample / no charge
                                {selectedOrder?.sampleReason ? ` • ${selectedOrder.sampleReason}` : ''}
                              </Typography>
                            )}
                            {selectedOrder.notes && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Client notes: {selectedOrder.notes}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Alert severity="warning">
                            Select a carrier option in the Pack step before generating the label.
                          </Alert>
                        )}
                      </Box>

                      {trackingNumber && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Tracking Number: {trackingNumber}
                          </Typography>
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {!trackingNumber ? (
                          <Button
                            variant="contained"
                            startIcon={<ShipIcon />}
                            onClick={handleGenerateLabel}
                            disabled={!selectedCarrierCode || !selectedServiceCode}
                            sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                          >
                            Generate {getCarrierDisplayName(selectedCarrierCode || 'Carrier')} Label
                          </Button>
                        ) : (
                          <>
                            {labelData && (
                              <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={handlePrintLabel}
                                sx={{
                                  borderColor: '#039582',
                                  color: '#039582',
                                  '&:hover': { borderColor: '#027A6B' }
                                }}
                              >
                                Download & Print Label
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              startIcon={<CompleteIcon />}
                              onClick={handleCompleteOrder}
                              sx={{
                                bgcolor: '#10b981',
                                '&:hover': { bgcolor: '#059669' }
                              }}
                            >
                              Complete Order
                            </Button>
                          </>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                </Stepper>
              </Grid>
            </Grid>
          )}
                 </DialogContent>
       </Dialog>

       {/* Line Items Adjustment Dialog */}
       <Dialog
         open={lineItemsDialogOpen}
         onClose={handleCloseLineItemsDialog}
         maxWidth="md"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
           }
         }}
       >
         <DialogTitle sx={{ 
           bgcolor: '#039582', 
           color: 'white',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'space-between',
           pb: 2
         }}>
           <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
             Adjust Line Items
           </Typography>
           <IconButton 
             onClick={handleCloseLineItemsDialog} 
             sx={{ 
               color: 'white',
               '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
             }}
           >
             <CloseIcon />
           </IconButton>
         </DialogTitle>

         <DialogContent sx={{ mt: 2, pb: 1 }}>
           {lineItemsMessage && (
             <Alert
               severity={lineItemsMessage.severity}
               sx={{ mb: 2 }}
               onClose={() => setLineItemsMessage(null)}
             >
               {lineItemsMessage.text}
             </Alert>
           )}

           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             Adjust quantities and prices for each line item. You can split items into multiple line items with different prices.
           </Typography>

           <Box sx={{ mb: 2 }}>
             {adjustedLineItems.map((item, index) => (
               <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
                 <CardContent>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                     <Box>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                         {item.productName}
                       </Typography>
                       <Typography variant="caption" color="text.secondary">
                         SKU: {item.sku || 'N/A'}
                       </Typography>
                     </Box>
                     {adjustedLineItems.length > 1 && (
                       <IconButton
                         size="small"
                         color="error"
                         onClick={() => handleRemoveLineItem(item.id)}
                         disabled={updatingLineItems}
                       >
                         <CloseIcon />
                       </IconButton>
                     )}
                   </Box>

                   <Grid container spacing={2}>
                     <Grid item xs={12} sm={4}>
                       <TextField
                         label="Quantity"
                         type="number"
                         value={item.quantity}
                         onChange={(e) => handleUpdateLineItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                         inputProps={{ min: 1 }}
                         fullWidth
                         disabled={updatingLineItems}
                       />
                     </Grid>
                     <Grid item xs={12} sm={4}>
                       <TextField
                         label="Unit Price ($)"
                         type="number"
                         value={item.unitPrice}
                         onChange={(e) => handleUpdateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                         inputProps={{ min: 0, step: 0.01 }}
                         fullWidth
                         disabled={updatingLineItems}
                         InputProps={{
                           startAdornment: <InputAdornment position="start">$</InputAdornment>
                         }}
                       />
                     </Grid>
                     <Grid item xs={12} sm={4}>
                       <TextField
                         label="Total Price ($)"
                         type="number"
                         value={item.totalPrice.toFixed(2)}
                         onChange={(e) => handleUpdateLineItem(item.id, 'totalPrice', parseFloat(e.target.value) || 0)}
                         inputProps={{ min: 0, step: 0.01 }}
                         fullWidth
                         disabled={updatingLineItems}
                         InputProps={{
                           startAdornment: <InputAdornment position="start">$</InputAdornment>
                         }}
                       />
                     </Grid>
                   </Grid>
                 </CardContent>
               </Card>
             ))}
           </Box>

           <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
             <Button
               variant="outlined"
               startIcon={<AddIcon />}
               onClick={handleAddLineItem}
               disabled={updatingLineItems || !selectedOrder}
               sx={{
                 borderColor: '#039582',
                 color: '#039582',
                 '&:hover': { borderColor: '#027A6B', bgcolor: '#f0fdfa' }
               }}
             >
               Add Line Item
             </Button>
           </Box>

           <Divider sx={{ my: 2 }} />

           <Box sx={{ bgcolor: '#f7f7f8', p: 2, borderRadius: 1 }}>
             <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
               Summary
             </Typography>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
               <Typography variant="body2">Subtotal:</Typography>
               <Typography variant="body2" sx={{ fontWeight: 600 }}>
                 ${adjustedLineItems.reduce((sum, item) => sum + item.totalPrice, 0).toFixed(2)}
               </Typography>
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
               <Typography variant="body2">Shipping:</Typography>
               <Typography variant="body2" sx={{ fontWeight: 600 }}>
                 ${(selectedOrder?.shippingAmount || 0).toFixed(2)}
               </Typography>
             </Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
               <Typography variant="body2">Tax:</Typography>
               <Typography variant="body2" sx={{ fontWeight: 600 }}>
                 ${(selectedOrder?.taxAmount || 0).toFixed(2)}
               </Typography>
             </Box>
             <Divider sx={{ my: 1 }} />
             <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
               <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                 Total:
               </Typography>
               <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#039582' }}>
                 ${(
                   adjustedLineItems.reduce((sum, item) => sum + item.totalPrice, 0) +
                   (selectedOrder?.shippingAmount || 0) +
                   (selectedOrder?.taxAmount || 0)
                 ).toFixed(2)}
               </Typography>
             </Box>
           </Box>
         </DialogContent>

         <DialogActions sx={{ p: 2, pt: 1 }}>
           <Button
             onClick={handleCloseLineItemsDialog}
             disabled={updatingLineItems}
             sx={{ color: '#666' }}
           >
             Cancel
           </Button>
           <Button
             variant="contained"
             onClick={handleSaveLineItems}
             disabled={updatingLineItems || adjustedLineItems.length === 0}
             sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
           >
             {updatingLineItems ? 'Saving...' : 'Save Line Items'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Error Dialog for Label Generation Issues */}
       <Dialog
         open={errorDialogOpen}
         onClose={() => setErrorDialogOpen(false)}
         maxWidth="sm"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
           }
         }}
       >
         <DialogTitle sx={{ 
           bgcolor: '#ef4444', 
           color: 'white',
           display: 'flex',
           alignItems: 'center',
           gap: 1.5,
           pb: 2
         }}>
           <ErrorIcon sx={{ fontSize: 28 }} />
           <Typography variant="h6" component="div" sx={{ fontWeight: 600, flex: 1 }}>
             {errorDialogTitle}
           </Typography>
           <IconButton 
             onClick={() => setErrorDialogOpen(false)} 
             sx={{ 
               color: 'white',
               '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
             }}
           >
             <CloseIcon />
           </IconButton>
         </DialogTitle>

         <DialogContent sx={{ mt: 2, pb: 1 }}>
           <Alert 
             severity="error" 
             sx={{ 
               mb: 3,
               '& .MuiAlert-icon': {
                 fontSize: 24
               }
             }}
           >
             <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
               {errorDialogMessage}
             </Typography>
           </Alert>

           <Box sx={{ 
             p: 2, 
             bgcolor: '#fef2f2', 
             borderRadius: 1,
             border: '1px solid #fecaca',
             mb: 2
           }}>
             <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
               {errorDialogDetails}
             </Typography>
           </Box>

           {errorDialogTitle.includes('Insufficient') && (
             <Box sx={{ 
               p: 2, 
               bgcolor: '#eff6ff', 
               borderRadius: 1,
               border: '1px solid #bfdbfe',
               mt: 2
             }}>
               <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                 Next Steps:
               </Typography>
               <List dense sx={{ py: 0 }}>
                 <ListItem sx={{ px: 0, py: 0.5 }}>
                   <ListItemText 
                     primary="1. Log into your ShipStation account"
                     primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                   />
                 </ListItem>
                 <ListItem sx={{ px: 0, py: 0.5 }}>
                   <ListItemText 
                     primary="2. Navigate to Billing & Account Settings"
                     primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                   />
                 </ListItem>
                 <ListItem sx={{ px: 0, py: 0.5 }}>
                   <ListItemText 
                     primary="3. Add funds to your account balance"
                     primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                   />
                 </ListItem>
                 <ListItem sx={{ px: 0, py: 0.5 }}>
                   <ListItemText 
                     primary="4. Return here and try generating the label again"
                     primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                   />
                 </ListItem>
               </List>
             </Box>
           )}
         </DialogContent>

         <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
           <Button
             onClick={() => setErrorDialogOpen(false)}
             variant="contained"
             sx={{
               bgcolor: '#ef4444',
               color: 'white',
               '&:hover': { bgcolor: '#dc2626' },
               px: 3,
               textTransform: 'none',
               fontWeight: 600
             }}
           >
             I Understand
           </Button>
         </DialogActions>
       </Dialog>

       {/* Completion Dialog */}
       <Dialog
         open={completionDialogOpen}
         onClose={handleCompletionDialogClose}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle sx={{ bgcolor: '#10b981', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5 }}>
           <SuccessIcon sx={{ fontSize: 28 }} />
           <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
             Order Completed
           </Typography>
           <IconButton onClick={handleCompletionDialogClose} sx={{ color: 'white' }}>
             <CloseIcon />
           </IconButton>
         </DialogTitle>
         <DialogContent sx={{ py: 3 }}>
           <Alert severity="success" sx={{ mb: 2 }}>
             <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
               Great job! This order has been marked as shipped.
             </Typography>
           </Alert>
           {completedOrderInfo && (
             <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 2, p: 2, border: '1px solid #bbf7d0' }}>
               <Typography variant="body2" color="text.secondary">
                 Order Number
               </Typography>
               <Typography variant="h6" sx={{ fontWeight: 700, color: '#047857', mb: 2 }}>
                 {completedOrderInfo.orderNumber}
               </Typography>
               <Grid container spacing={2}>
                 <Grid item xs={12} sm={6}>
                   <Typography variant="body2" color="text.secondary">
                     Total Amount
                   </Typography>
                   <Typography variant="body1" sx={{ fontWeight: 600 }}>
                     {completedOrderInfo.totalAmount ? formatCurrency(completedOrderInfo.totalAmount) : '—'}
                   </Typography>
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <Typography variant="body2" color="text.secondary">
                     Tracking Number
                   </Typography>
                   <Typography variant="body1" sx={{ fontWeight: 600 }}>
                     {completedOrderInfo.trackingNumber || 'Pending Carrier Pickup'}
                   </Typography>
                 </Grid>
               </Grid>
             </Box>
           )}
         </DialogContent>
         <DialogActions sx={{ px: 3, pb: 3 }}>
           <Button
             onClick={handleCompletionDialogClose}
             variant="contained"
             sx={{
               bgcolor: '#10b981',
               '&:hover': { bgcolor: '#059669' },
               textTransform: 'none',
               fontWeight: 600
             }}
           >
             Continue to Order Queue
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };
 
 export default OrderProcessing;

